import { Link as RouterLink } from "react-router-dom";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import { Eye, Mail, Plus, Search } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  ActionMenu,
  AsyncSection,
  asyncStatus,
  Button,
  ConfirmDialog,
  DataTable,
  DatePicker,
  DescriptionItem,
  DescriptionList,
  DiscardChangesDialog,
  EmptyState,
  FilterBar,
  FormField,
  Input,
  Link,
  Modal,
  Pagination,
  SectionHeader,
  Select,
  Skeleton,
  StatTile,
  StatusPill,
  Switch,
  TabPanel,
  Tabs,
  Textarea,
  SettingsMetaCard,
  useCursorPagination,
  useRetainedValue,
} from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import {
  AdminAboutCard,
  AdminRelatedCard,
  AdminSection,
} from "./AdminSection.js";
import { useAuthStore } from "../../auth/auth-store.js";
import {
  getMailDomainStatus,
  mailDomainStatusQueryKey,
} from "../../api/mail-domain-status-api.js";
import {
  MailApiError,
  activateMailSender,
  archiveMailSender,
  createMailSender,
  disableMailSender,
  discardMailSender,
  enableMailSenderAndSwitch,
  getMailCapabilities,
  getMailSendingSettings,
  getMailMessage,
  listMailMessages,
  mailDeliveriesQueryKey,
  mailCapabilitiesQueryKey,
  mailMessageQueryKey,
  mailMessagesQueryKey,
  mailPreviewQueryKey,
  mailSendersQueryKey,
  mailSendingSettingsQueryKey,
  tenantMailSendingSettingsQueryKey,
  previewMailMessage,
  previewMailMessageDraft,
  revertMailMessageOverride,
  restoreMailSender,
  saveMailMessageOverride,
  searchMailDeliveries,
  testMailSender,
  updateMailSender,
  type MailDelivery,
  type MailDeliverySearchRequest,
  type MailMessageOverrideRequest,
  type MailMessagePreview,
  type MailMessageSummary,
  type MailSecurityMode,
  type MailSender,
  type MailSenderListGroup,
} from "../../api/mail-api.js";
import {
  channelOf,
  listNotificationPolicy,
  notificationPolicyQueryKey,
  setNotificationPolicyChannel,
  type NotificationChannel,
  type NotificationPolicyEvent,
} from "../../api/notification-policy-api.js";
import {
  humanise,
  labelForModule,
  labelForNotificationType,
} from "../../notifications/notification-event-labels.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";
import { SenderRecordsPanel } from "./SenderRecordsPanel.js";
import { useAllMailSenderPages } from "./useAllMailSenderPages.js";
import {
  activeSender,
  deriveEmailReach,
  isInventorySettled,
} from "./useMailSendingState.js";
import { ADMIN_SECTION_ROUTES } from "../../routes.js";
import {
  isoDateInZone,
  startOfDayInZone,
  useDateTimeFormatter,
} from "../../settings/format-date-time.js";

/* Email & notifications (PB-2925 / AC-14).

   The deployment is customer-hosted, so PlaybookAI sends through the CUSTOMER's
   mail server from the CUSTOMER's domain. There is deliberately no
   PlaybookAI-operated fallback sender — where nothing is active here, nothing
   is sent from anywhere, and this page says so rather than letting an
   administrator assume mail is going out.

   Admin-only via the `/admin` parent gate (RequireTenantAdmin, PB-48); the
   notification service re-checks `tenant-admin` on every route and remains the
   authoritative gate. */

/* `satisfies`, so a typo in a value is a typecheck error rather than a select
   that silently posts something the server rejects. */
const SECURITY_OPTIONS = [
  { value: "starttls", label: "STARTTLS (usually port 587)" },
  { value: "tls", label: "TLS (usually port 465)" },
  { value: "none", label: "No encryption (not recommended)" },
] satisfies readonly { value: MailSecurityMode; label: string }[];

/**
 * The same three modes, said short enough for a card's meta line.
 *
 * The cards used to render `securityMode.toUpperCase()`, which put the raw
 * enum `NONE` on screen for the one mode an administrator most needs to
 * understand — and contradicted the form, which calls it "No encryption". The
 * select's own labels carry a parenthetical port hint that belongs in a form
 * and not in a one-line summary, so this is a second map rather than a reuse
 * of `SECURITY_OPTIONS`.
 */
const SECURITY_LABEL = {
  starttls: "STARTTLS",
  tls: "TLS",
  none: "No encryption",
} satisfies Record<MailSecurityMode, string>;

const SENDER_PAGE_SIZE = 10;
const MESSAGE_PAGE_SIZE = 10;

/** What sends each MVP message, in an administrator’s words. */
const MESSAGE_TRIGGERS: Readonly<Record<string, string>> = {
  invitation: "Invitation created",
  set_password: "Email verified",
  access_granted: "Access assigned",
  export_ready: "Export completed",
};

/**
 * What the Event column says a delivery was (PB-5407).
 *
 * Three kinds of row share this column and only two of them used to be
 * distinguishable. An ADDRESSED email — an invitation, a password email, an
 * export — is deliberately not a registry event, so it has no `eventType`; the
 * column read that null as "no event" and labelled every one of them
 * "Connection test", on the screen an administrator goes to in order to confirm
 * an invitation went out.
 *
 * The order matters. `messageKey` is checked FIRST because it is the most
 * specific claim: only an addressed send has one.
 *
 * An unknown key falls back to a readable name rather than to "Connection
 * test". The defect here was a WRONG label, not a missing one, so a fallback
 * that lands back on the connection-test wording would reintroduce it for the
 * next addressed message added to the catalogue.
 *
 * "Connection test" is NOT the residue of the other two being absent, which is
 * what this first shipped as and what the review caught. An addressed email
 * recorded before `messageKey` existed has no key and no event type either, so
 * that rule called real customer mail a test send — for the very rows this
 * ticket was raised about. It is now an explicit claim the server makes from
 * the producer key, and nothing else answers it.
 *
 * What is left is an addressed email whose cause was never recorded. It gets an
 * honest generic name: we know it went straight to a person rather than as a
 * notification fan-out, and we do not know why, so the label says the first and
 * does not guess the second.
 *
 * Kept local rather than shared: it is one ordered conditional over this page's
 * own copy, and a module wrapping it would expose about as much surface as it
 * hides.
 */
function deliveryEventLabel(delivery: MailDelivery): string {
  /* `?? null` because the field is OPTIONAL on the wire: a notification service
     older than this field omits it, and absent has to mean the same as null. */
  const messageKey = delivery.messageKey ?? null;
  if (messageKey !== null) {
    return MESSAGE_TRIGGERS[messageKey] ?? humanise(messageKey);
  }
  if (delivery.eventType !== null) {
    return labelForNotificationType(delivery.eventType).title;
  }
  if (delivery.isConnectionTest === true) return "Connection test";
  return "Direct email";
}

/* One source for the Mail server State filter's words, shared by the Select and
   the removable filter chip. Two copies drift, and the drift is silent: a chip
   that names the wrong state still looks like a working filter. */
const SENDER_STATE_LABELS: Readonly<Record<string, string>> = {
  active: "Active",
  draft: "Draft",
  disabled: "Turned off",
  test_failed: "Test failed",
  archived: "Archived",
  superseded: "Previous",
};

/**
 * Which sender an action belongs to, said in the button's accessible name.
 *
 * A zone can hold more than one record — two turned-off servers is the ordinary
 * case — and every card offers the same labels, so "Send test message, button"
 * announced twice in a row identifies neither. The visible text stays the start
 * of the name (WCAG 2.5.3 Label in Name), with the address appended.
 */
const actionName = (label: string, sender: MailSender): string => {
  /* Every fact the row SHOWS, in the order its columns show them, because no
     single one of them is unique. Two servers can share a display name (the
     case this started from); nothing makes `fromAddress` unique either, since
     replacing a mail server while keeping `no-reply@company.com` leaves the
     replacement and the row it replaced sharing an address; and two drafts can
     share both, which leaves the host and the state to separate them.
   *
     The row id would be unique and is deliberately NOT used: this string is
     read aloud, and a 36-character identifier is hostile to the reader it
     exists for. What remains after all four facts match is a row duplicated in
     every visible respect, which no name derived from visible content can
     separate — and neither can a sighted reader.
   *
     The display name is dropped when it IS the address: the server falls back
     to `fromAddress` when a sender has no name of its own, and "no-reply@x.com,
     no-reply@x.com" reads as a stutter rather than as two facts. */
  const parts = [
    sender.resolvedDisplayName === sender.fromAddress
      ? null
      : sender.resolvedDisplayName,
    `${sender.host}:${sender.port}`,
    sender.fromAddress,
    SENDER_STATE_LABELS[sender.status] ?? sender.status,
  ].filter((part): part is string => Boolean(part));
  return `${label}: ${parts.join(", ")}`;
};

/**
 * Return the durable control that opened the next layer.
 *
 * An ActionMenu item is only the selected command: its popover leaves the DOM
 * after selection. Restoring focus to that short-lived item races the popover's
 * exit transition and can ultimately drop focus on <body>. The open trigger is
 * the element whose aria-controls points at the active menu, so remember that
 * stable button instead. Direct buttons (such as Add mail server) remain their
 * own opener.
 */
function currentLayerOpener(): HTMLElement | null {
  const active =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  const menu = active?.closest<HTMLElement>('[role="menu"]');
  if (!menu?.id) return active;

  return (
    Array.from(document.querySelectorAll<HTMLElement>("[aria-controls]")).find(
      (candidate) => candidate.getAttribute("aria-controls") === menu.id,
    ) ?? active
  );
}

const EMPTY_FORM = {
  displayName: "",
  host: "",
  port: "587",
  securityMode: "starttls" as MailSecurityMode,
  username: "",
  secretReference: "",
  fromAddress: "",
  fromDisplayName: "",
  dkimSelector: "",
};

type SenderFieldErrors = Partial<Record<keyof typeof EMPTY_FORM, string>>;

/**
 * Which OTHER box's message a correction can settle.
 *
 * One rule in `validateSenderForm` blames a box the administrator is not told
 * to edit: a username without encryption is reported against `securityMode`,
 * because that is the box the server names, while the message offers clearing
 * the USERNAME as the other remedy. Clearing only the edited key therefore left
 * a Security error standing on a form that was already valid, until the next
 * submit — and the two likely reactions both cost something: switch a server
 * that genuinely needs no encryption over to STARTTLS to make the message go
 * away, or assume the form is still blocked and stop.
 *
 * One direction only. The reverse entry would be wrong: the sole `username`
 * message is the length rule, which changing Security cannot fix.
 */
const PAIRED_ERROR_FIELD: Partial<
  Record<keyof typeof EMPTY_FORM, keyof typeof EMPTY_FORM>
> = { username: "securityMode" };

/**
 * Which box is wrong, and why — before the request goes anywhere.
 *
 * The server validates all of this and is the authority; nothing here is a
 * substitute for it. What it changes is WHERE the answer lands: the page's only
 * feedback used to be one banner reading "Check the configuration" above eight
 * filled boxes, so an administrator was told their configuration was wrong and
 * never which part. Three unusable rows accumulated on dev behind that banner.
 *
 * The rules mirror `mail.schema.ts` deliberately, and a mismatch fails safe in
 * one direction only: anything this accepts the server still re-checks, and its
 * message is rendered as-is. So the risk of drift is a rule stated twice, not a
 * rule bypassed.
 *
 * `secretReference` is the exception and is checked loosely on purpose. The server
 * parses it with the resolver's OWN parser — a cloud secret reference, with the
 * rules that parser enforces — which lives in the notification service and
 * cannot be imported here. Approximating it would be a second, weaker rule
 * pretending to be the real one, so this catches only the paste errors that are
 * unambiguous and leaves the rest to the server.
 */
const HOST_PATTERN =
  /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const DKIM_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;

function validateSenderForm(form: typeof EMPTY_FORM): SenderFieldErrors {
  const errors: SenderFieldErrors = {};
  if (form.displayName.trim().length > 120)
    errors.displayName =
      "A server display name cannot be longer than 120 characters.";
  const host = form.host.trim();
  if (host === "") errors.host = "A mail server address is required.";
  else if (host.length > 253)
    errors.host = "A mail server address cannot be longer than 253 characters.";
  else if (!HOST_PATTERN.test(host))
    errors.host =
      "Enter a host name on its own — no https:// and no path after it.";

  const port = Number(form.port.trim());
  if (form.port.trim() === "") errors.port = "A port is required.";
  else if (!Number.isInteger(port) || port < 1 || port > 65535)
    errors.port = "Port must be a whole number between 1 and 65535.";

  const username = form.username.trim();
  if (username.length > 320)
    errors.username = "A username cannot be longer than 320 characters.";
  /* The cross-field rule, said HERE rather than after a round trip. A credential
     on an unencrypted connection crosses the network in clear text, and the
     server refuses it — but the refusal arrives against `securityMode`, which is
     not the box the administrator was last looking at. */ else if (
    form.securityMode === "none" &&
    username !== ""
  )
    errors.securityMode =
      "A username cannot be used without encryption — the password would cross the network in clear text. Choose STARTTLS or TLS, or clear the username.";

  const secretReference = form.secretReference.trim();
  if (secretReference === "")
    errors.secretReference = "A secret reference is required.";
  else if (
    !/^https:\/\//iu.test(secretReference) &&
    !secretReference.startsWith("arn:")
  )
    errors.secretReference =
      "Use an Azure Key Vault secret URL or AWS Secrets Manager ARN.";
  else if (
    /^https:\/\//iu.test(secretReference) &&
    /^\/secrets\/[^/]+\/[^/]+\/?$/u.test(
      (() => {
        try {
          return new URL(secretReference).pathname;
        } catch {
          return "";
        }
      })(),
    )
  )
    errors.secretReference =
      "Remove the secret version from the Azure Key Vault URL so password rotation keeps working.";

  const fromAddress = form.fromAddress.trim();
  if (fromAddress === "")
    errors.fromAddress = "A send-from address is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fromAddress))
    errors.fromAddress =
      "Enter a single email address, like no-reply@example.com.";

  if (form.fromDisplayName.trim().length > 120)
    errors.fromDisplayName =
      "A sender display name cannot be longer than 120 characters.";

  const dkim = form.dkimSelector.trim();
  if (dkim !== "" && (dkim.length > 63 || !DKIM_PATTERN.test(dkim)))
    errors.dkimSelector =
      "A DKIM selector is a single DNS label — letters, digits and hyphens, not starting or ending with one.";

  return errors;
}

const SENDER_FIELD_ORDER = Object.keys(
  EMPTY_FORM,
) as (keyof typeof EMPTY_FORM)[];

function focusFirstInvalid(
  form: HTMLFormElement,
  errors: SenderFieldErrors,
): void {
  const first = SENDER_FIELD_ORDER.find((field) => errors[field]);
  if (!first) return;
  const field = form.querySelector<HTMLElement>(`[data-field="${first}"]`);
  field?.querySelector<HTMLElement>("input, button, textarea, select")?.focus();
}

/** The eight form boxes, as the server wants them. */
function toUpdateRequest(form: typeof EMPTY_FORM, port: number) {
  return {
    displayName: form.displayName.trim() || null,
    host: form.host.trim(),
    port,
    securityMode: form.securityMode,
    username: form.username.trim() || null,
    secretReference: form.secretReference.trim(),
    fromAddress: form.fromAddress.trim(),
    fromDisplayName: form.fromDisplayName.trim() || null,
    /* ALWAYS sent, unlike on create. A PUT replaces the whole object, so an
       omitted key clears the stored value — which is right when the box was
       emptied, and wrong if it were omitted to mean "leave it alone". */
    dkimSelector: form.dkimSelector.trim() || null,
  };
}

/** A stored sender, back into the boxes that produced it. */
function senderToForm(sender: MailSender): typeof EMPTY_FORM {
  return {
    displayName: sender.displayName ?? "",
    host: sender.host,
    port: String(sender.port),
    securityMode: sender.securityMode,
    username: sender.username ?? "",
    secretReference: sender.secretReference ?? sender.secretUri,
    fromAddress: sender.fromAddress,
    fromDisplayName: sender.fromDisplayName ?? "",
    dkimSelector: sender.dkimSelector ?? "",
  };
}

/** What each editor action is called when it fails.
 *
 * One shared failure slot, three actions. A single hard-coded title announced a
 * failed preview or a failed restore as a save that could not be made — naming
 * an action the administrator had not asked for, which is its own small lie
 * about what the server refused (PB-4987).
 */
const FAILURE_TITLES: Record<"save" | "preview" | "restore", string> = {
  save: "That change cannot be saved",
  preview: "That draft cannot be previewed",
  restore: "The built-in wording cannot be restored",
};

function errorCopy(error: unknown): string {
  if (error instanceof MailApiError) return error.message;
  return error instanceof Error
    ? error.message
    : "Something went wrong. Try again.";
}

export function CommunicationAdmin(): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTime, dateTimeOr } = formatter;
  const queryClient = useQueryClient();
  const editorTriggerRef = useRef<HTMLElement | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<"create" | "edit" | null>(null);
  const [confirmEditorDiscard, setConfirmEditorDiscard] = useState(false);
  /* SEPARATE state from the add form, deliberately. Editing a draft while a new
     configuration is half typed must not discard either one — and the add form
     is the one an administrator would lose most, since it holds eight fields
     they have not saved anywhere. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editInitial, setEditInitial] = useState(EMPTY_FORM);
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmDiscardId, setConfirmDiscardId] = useState<string | null>(null);
  const [testTarget, setTestTarget] = useState<MailSender | null>(null);
  const [testResult, setTestResult] = useState<MailSender | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState<{
    sender: MailSender;
    kind: "activate" | "enable";
  } | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<MailSender | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<MailSender | null>(null);
  /* PB-5169. The server whose last test is open, or null. Stable opener so the
     columns memo below is not rebuilt on every render. */
  const [testDetail, setTestDetail] = useState<MailSender | null>(null);
  const onOpenTestDetail = useCallback(
    (row: MailSender) => setTestDetail(row),
    [],
  );
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [tab, setTab] = useState("server");
  const [historyEvent, setHistoryEvent] = useState<string | null>(null);
  const [senderKeyword, setSenderKeyword] = useState("");
  const [senderStatus, setSenderStatus] = useState("");
  const [senderPage, setSenderPage] = useState(1);
  /* Per-box errors, and separate per form for the same reason the forms are:
     an edit's problems must not appear against the add form's boxes. */
  const [fieldErrors, setFieldErrors] = useState<SenderFieldErrors>({});
  const [editFieldErrors, setEditFieldErrors] = useState<SenderFieldErrors>({});

  /* Cleared as the box is corrected, not on the next submit. The others stay,
     so a form with three problems does not reveal them one at a time. */
  const forgetFieldError =
    (setter: typeof setFieldErrors) =>
    (field: keyof typeof EMPTY_FORM): void =>
      setter((previous) => {
        const paired = PAIRED_ERROR_FIELD[field];
        const stale = (paired === undefined ? [field] : [field, paired]).filter(
          (key) => previous[key] !== undefined,
        );
        if (stale.length === 0) return previous;
        const next = { ...previous };
        for (const key of stale) delete next[key];
        return next;
      });

  /* Same tenant fence the sender-records panel uses, so `refresh` invalidates
     the exact key that panel reads. */
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  /* ONE table, driven by the State filter — which is what removed three
     screens of scroll from the bottom of this tab.
   *
     "Previous servers" and "Archived servers" used to be separate collapsed
     sections below the table, each with its own lazily-enabled query, its own
     Card tiles instead of rows, and its own "Load 5 more" button instead of
     shared Pagination. Three list UIs for one concept, stacked vertically, and
     the two that mattered least took the most vertical space. They are states
     of a mail server, so they belong in the State filter next to the others.
   *
     The group is derived rather than fetched all at once: the default view
     still loads only operational rows, so making archived reachable costs
     nothing until somebody asks for it. */
  const senderGroup: Exclude<MailSenderListGroup, "default"> =
    senderStatus === "archived"
      ? "archived"
      : senderStatus === "superseded"
        ? "previous"
        : "operational";
  /* TWO queries, and the split is load-bearing rather than tidy.
   *
     `operational` is always mounted because the ACTIVE sender is a page-level
     fact: the header switch, its StatusPill and the health summary all read it.
     Deriving it from the filtered rows instead would make the page claim "no
     active server" the moment an administrator filtered the table to Archived —
     a table filter silently rewriting the page's own status.
   *
     The second query carries whichever non-operational group is selected and
     stays unfetched until one is, so the default view costs exactly what it did
     before. */
  const operational = useAllMailSenderPages("operational");
  const alternate = useAllMailSenderPages(
    senderGroup === "operational" ? "archived" : senderGroup,
    "admin",
    senderGroup !== "operational",
  );
  const senders = senderGroup === "operational" ? operational : alternate;
  const capabilities = useQuery({
    queryKey: mailCapabilitiesQueryKey,
    queryFn: getMailCapabilities,
  });
  const sendingSettings = useQuery({
    /* Keyed by tenant, as the dialogs' copy of this read is — one cache entry
       per tenant, shared by both surfaces. `refresh` below invalidates by the
       unscoped prefix, which still reaches it. */
    queryKey: tenantMailSendingSettingsQueryKey(tenantId),
    queryFn: getMailSendingSettings,
    enabled: capabilities.data?.sendingControl.enabled === true,
  });
  /* The two separate `previous` / `archived` infinite queries that used to live
     here are gone with the collapsed sections they fed. `useAllMailSenderPages`
     now takes the group the State filter selects, so there is one query, one
     list and one pagination control rather than three of each. */
  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: mailSendersQueryKey }),
      queryClient.invalidateQueries({ queryKey: mailDeliveriesQueryKey }),
      /* The sender-records panel is mounted on this same page and keys off the
         ACTIVE sender's from-domain, so creating, activating or disabling a
         sender changes which domain it is about. Without this it kept showing
         the previous domain's verdicts — or "not verified" for a domain that
         had just been replaced — until something else happened to refetch. */
      queryClient.invalidateQueries({
        queryKey: mailDomainStatusQueryKey(tenantId),
      }),
      /* The sending state is DERIVED from whether an active, test-passing
         sender exists whenever the tenant has no explicit settings row — which
         is every tenant by default. So activating, disabling or testing a
         sender changes it, and nothing here was re-reading it: after an
         activation the page still showed "Sending paused" while the server
         already answered true.
       *
         It went unnoticed while the header carried a sending switch, because
         flipping that switch invalidated this key itself. Removing the switch
         removed the accidental refresh with it. */
      queryClient.invalidateQueries({
        queryKey: mailSendingSettingsQueryKey,
      }),
    ]);
  };

  const create = useMutation({
    mutationFn: createMailSender,
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      setFormError(null);
      setFieldErrors({});
      setEditorMode(null);
      await refresh();
    },
    onError: (error) => setFormError(errorCopy(error)),
  });

  const runTest = useMutation({
    mutationFn: testMailSender,
    onSuccess: async (result) => {
      setTestResult(result);
      setTestError(null);
      await refresh();
    },
    onError: (error) => setTestError(errorCopy(error)),
  });

  const activate = useMutation({
    mutationFn: ({
      senderId,
      supersedesId,
    }: {
      senderId: string;
      supersedesId: string | null;
    }) => activateMailSender(senderId, supersedesId),
    onSuccess: async () => {
      setActionError(null);
      setConfirmSwitch(null);
      await refresh();
    },
    onError: (error) => setActionError(errorCopy(error)),
  });

  /* A stale row is the one failure the copy cannot explain on its own: the
     server says the configuration does not exist while the administrator is
     looking straight at it. Refetching makes the message and the screen agree. */
  const onActionError = (error: unknown): void => {
    setActionError(errorCopy(error));
    if (error instanceof MailApiError && error.code === "mail_sender_not_found")
      void refresh();
  };

  const saveEdit = useMutation({
    mutationFn: ({
      senderId,
      input,
    }: {
      senderId: string;
      input: ReturnType<typeof toUpdateRequest>;
    }) => updateMailSender(senderId, input),
    /* `savedId` is React Query's `variables.senderId`, and the predicate is the
       same one `discard` below carries — for the same reason, reached a
       different way. A save is IN FLIGHT for a while, and no other draft's
       Edit button is disabled while it is, so an administrator can submit
       draft A, open draft B and start typing before A's response lands.
       Clearing unconditionally then closed B's editor and lost B's typing,
       with nothing on screen to say why. Only the editor that belongs to the
       row that just saved should close. */
    onSuccess: async (_result, { senderId: savedId }) => {
      setEditingId((current) => (current === savedId ? null : current));
      setEditError(null);
      setEditorMode(null);
      await refresh();
    },
    onError: (error) => setEditError(errorCopy(error)),
  });

  const discard = useMutation({
    mutationFn: discardMailSender,
    /* `discardedId` is React Query's `variables` — the id this mutation was
       called with. */
    onSuccess: async (_result, discardedId) => {
      setActionError(null);
      setConfirmDiscardId(null);
      /* ONLY when the open editor is the row that went away.

         It used to close unconditionally, and that broke the case it was not
         thinking about: the zone renders one card per draft, each with its own
         Discard button, and the confirmation is page-level — so editing draft
         A and discarding draft B unmounted A's form, and reopening it called
         `beginEdit`, which reloads the SERVER values. Whatever had been typed
         was gone, with no warning. Multiple drafts is the documented real
         state: three unusable rows is why edit exists at all.

         The case it WAS thinking about turns out not to need it. A draft being
         edited has no Discard button — its card is the form — so it cannot be
         discarded from here; and a discard by someone else drops it from
         `drafts`, which is what the form is rendered inside, so the form
         unmounts on its own. The predicate stays anyway: it keeps `editingId`
         from naming a row that no longer exists, which is cheap and true
         regardless of who renders what. */
      setEditingId((current) => (current === discardedId ? null : current));
      await refresh();
    },
    onError: onActionError,
  });

  const enable = useMutation({
    mutationFn: ({
      senderId,
      switchesFromId,
    }: {
      senderId: string;
      switchesFromId: string | null;
    }) => enableMailSenderAndSwitch(senderId, switchesFromId),
    onSuccess: async () => {
      setActionError(null);
      setConfirmSwitch(null);
      await refresh();
    },
    onError: onActionError,
  });

  const archive = useMutation({
    mutationFn: archiveMailSender,
    onSuccess: async () => {
      setActionError(null);
      setConfirmArchive(null);
      await refresh();
    },
    onError: onActionError,
  });

  const restore = useMutation({
    mutationFn: restoreMailSender,
    onSuccess: async () => {
      setActionError(null);
      setConfirmRestore(null);
      await refresh();
    },
    onError: onActionError,
  });

  const [confirmDisable, setConfirmDisable] = useState(false);
  const disable = useMutation({
    mutationFn: disableMailSender,
    onSuccess: () => {
      /* Opening the dialog already clears this, but a RETRY is made from the
         dialog that is already open — so the previous attempt's critical Alert
         survived the successful one, and an irreversible action reported
         failure after it had succeeded. The create form documents exactly this
         reasoning; the disable flow never got it (PB-4987 M9). */
      setActionError(null);
      setConfirmDisable(false);
      refresh();
    },
    onError: (error) => setActionError(errorCopy(error)),
  });

  const rows = senders.rows;
  /* FOUR allowlists, not three, and `discarded` is in none of them.
     That is the whole of how a discarded configuration stays off this page: it
     matches no bucket, so nothing renders it — no filter to get wrong, and no
     count to disclose what is being hidden. The server also excludes it from the
     Player AI projection, so it cannot arrive here as an LLM answer either. */
  /* From `operational`, NEVER from the filtered `rows` — see the two-query note
     above. This is the page's status, not the table's. Paired with
     `operational` for the same reason in the two reads below; the filtered
     `senders` query can be a different bucket entirely. */
  const active = activeSender(operational.rows);
  const activeServerKnown = isInventorySettled(operational);
  /* ONE derivation, read by all three surfaces here — the header pill, the
     health summary tile and the notification row status — and, since PB-5435,
     by both credential dialogs on Members & roles through `useMailSendingState`.
     The dialogs used to derive a second, weaker answer from the stored setting
     alone and said "queued" while this page said "Sending paused". Why the
     answer is ordered the way it is lives with `deriveEmailReach`. */
  const emailReach = deriveEmailReach({
    activeServerKnown,
    hasActiveServer: active !== null,
    capabilities,
    settings: sendingSettings,
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setActionError(null);
    /* Cleared HERE, not just in onSuccess. Otherwise a previous rejection's
       critical Alert sits above the form for the whole duration of the next
       request, so the administrator reads a stale failure while their new save
       is in flight. */
    setFormError(null);
    const problems = validateSenderForm(form);
    if (Object.keys(problems).length > 0) {
      setFieldErrors(problems);
      focusFirstInvalid(event.currentTarget, problems);
      return;
    }
    setFieldErrors({});
    const port = Number(form.port);
    const { dkimSelector, ...rest } = toUpdateRequest(form, port);
    create.mutate({
      ...rest,
      /* OMITTED when empty, not sent as null. `createMailSenderSchema` is
         `.strict()`, so during any rollout window where the shell is new and
         notification is not, an unknown key makes EVERY sender create fail with
         `invalid_mail_sender`. Omitting it keeps the common case (no selector
         configured) working against either version — the same wave-gap hazard
         this repo documents for migration/deploy ordering, arriving through a
         request field instead of a column.

         The EDIT path does the opposite and sends it always, because a PUT
         replaces the whole object. See `toUpdateRequest`. */
      ...(dkimSelector === null ? {} : { dkimSelector }),
    });
  };

  const submitEdit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setEditError(null);
    const problems = validateSenderForm(editForm);
    if (Object.keys(problems).length > 0) {
      setEditFieldErrors(problems);
      focusFirstInvalid(event.currentTarget, problems);
      return;
    }
    setEditFieldErrors({});
    const port = Number(editForm.port);
    if (!editingId) return;
    saveEdit.mutate({
      senderId: editingId,
      input: toUpdateRequest(editForm, port),
    });
  };

  const beginEdit = (sender: MailSender): void => {
    editorTriggerRef.current = currentLayerOpener();
    setActionError(null);
    setEditError(null);
    setEditFieldErrors({});
    setEditingId(sender.id);
    const next = senderToForm(sender);
    setEditForm(next);
    setEditInitial(next);
    setEditorMode("edit");
  };

  const beginCreate = (): void => {
    editorTriggerRef.current = currentLayerOpener();
    setForm(EMPTY_FORM);
    setFormError(null);
    setFieldErrors({});
    setEditingId(null);
    setEditorMode("create");
  };

  const closeEditor = (): void => {
    setEditorMode(null);
    setEditingId(null);
    setFormError(null);
    setEditError(null);
    setFieldErrors({});
    setEditFieldErrors({});
    setConfirmEditorDiscard(false);
  };

  /* A dirty editor and its confirmation are nested modal layers. Closing both
     in one render lets the inner layer restore focus to the editor's Cancel
     button after the editor has started leaving the DOM. Remember the real
     opener and restore it after React has flushed both clean-ups. */
  const discardEditorChanges = (): void => {
    const trigger = editorTriggerRef.current;
    closeEditor();
    window.setTimeout(() => {
      if (trigger?.isConnected) trigger.focus();
    }, 0);
  };

  const editorDirty =
    editorMode === "create"
      ? JSON.stringify(form) !== JSON.stringify(EMPTY_FORM)
      : editorMode === "edit"
        ? JSON.stringify(editForm) !== JSON.stringify(editInitial)
        : false;
  const editorSaving = create.isPending || saveEdit.isPending;
  const requestEditorClose = (): void => {
    if (editorSaving) return;
    if (editorDirty) setConfirmEditorDiscard(true);
    else closeEditor();
  };

  const beginTest = (sender: MailSender): void => {
    setActionError(null);
    setTestTarget(sender);
    setTestResult(null);
    setTestError(null);
    runTest.mutate(sender.id);
  };

  const filteredSenders = useMemo(() => {
    const term = senderKeyword.trim().toLocaleLowerCase();
    /* The ACTIVE server first, always.
     *
       It is the one row that answers the question the page is open for — which
       server is sending — and it arrived wherever the server's own ordering put
       it, so on a workspace with a few drafts it could sit below them and below
       the fold. Everything else keeps the order it came in: `sort` is stable, so
       this promotes one row rather than reshuffling the rest, and a list that
       reorders itself for any other reason is harder to scan, not easier.
     *
       Sorting a COPY — `filter` already returns a new array, but relying on
       that is relying on an implementation detail of the line above; an
       in-place sort of `rows` would mutate the query cache. */
    const matches = rows.filter((sender) => {
      const matchesKeyword =
        term === "" ||
        [
          sender.resolvedDisplayName,
          sender.displayName ?? "",
          sender.fromAddress,
          sender.host,
        ].some((value) => value.toLocaleLowerCase().includes(term));
      /* THREE dimensions, not one.
       *
         Four of the ticket's state options are `MailSenderStatus` values, but
         "Test failed" is a test OUTCOME that can hold on an active or a
         turned-off server alike — comparing it to `sender.status` would match
         nothing and read as "no such servers" rather than as a filter that
         cannot work.
       *
         `archived` and `superseded` need no predicate at all: selecting them
         switched the QUERY to that group above, so every row already belongs.
         Re-filtering by status would be harmless but misleading to read. */
      const matchesState =
        senderStatus === "" ||
        senderStatus === "archived" ||
        senderStatus === "superseded" ||
        (senderStatus === "test_failed"
          ? sender.lastTestOutcome === "failed"
          : sender.status === senderStatus);
      return matchesKeyword && matchesState;
    });
    return [...matches].sort(
      (left, right) =>
        Number(right.status === "active") - Number(left.status === "active"),
    );
  }, [rows, senderKeyword, senderStatus]);
  const senderPageCount = Math.max(
    1,
    Math.ceil(filteredSenders.length / SENDER_PAGE_SIZE),
  );
  const currentSenderPage = Math.min(senderPage, senderPageCount);
  const visibleSenders = filteredSenders.slice(
    (currentSenderPage - 1) * SENDER_PAGE_SIZE,
    currentSenderPage * SENDER_PAGE_SIZE,
  );
  const clearSenderFilters = (): void => {
    setSenderKeyword("");
    setSenderStatus("");
    setSenderPage(1);
  };

  const senderColumns = useMemo(
    () => [
      {
        id: "sender",
        width: "22%",
        /* Column floors (PB-5436): each is this column's width at a 1024px window
           with the rail closed, so the table's minimum is its 1024px width. A
           narrower box (the Player AI rail at 1024-1152px) scrolls inside the
           pinned region instead of clipping; wider boxes are unchanged. */
        minWidth: 124,
        header: "Server",
        cardRole: "title" as const,
        cell: (sender: MailSender) => (
          <div
            className="flex min-w-0 flex-col"
            data-testid={
              sender.status === "active"
                ? "active-sender"
                : sender.status === "draft"
                  ? `draft-sender-${sender.id}`
                  : `turned-off-sender-${sender.id}`
            }
          >
            {/* Name only. The sender address used to be crammed in here as a
                subtitle; it is its own column now, which is what the ticket
                specifies and what makes the table scannable by sender. */}
            <span className="truncate text-dense-1 font-medium text-pb-text">
              {sender.resolvedDisplayName}
            </span>
          </div>
        ),
      },
      {
        id: "endpoint",
        width: "22%",
        minWidth: 124,
        header: "Address",
        cardRole: "subtitle" as const,
        cell: (sender: MailSender) => (
          <div className="flex flex-col">
            <span className="text-dense-1 text-pb-text">
              {sender.host}:{sender.port}
            </span>
            <span className="text-dense-2 text-pb-text-muted">
              {SECURITY_LABEL[sender.securityMode]}
            </span>
          </div>
        ),
      },
      {
        /* The ticket's column set is Server | Address | Sender | State | Last
           tested | Actions. "Secret provider" was an extra this page invented
           and is gone with this column taking its place — no loss, because the
           provider is server-derived metadata an administrator cannot act on,
           and its `?? "Azure Key Vault"` fallback also mislabelled any row
           whose optional provider was absent. */
        id: "from",
        width: "22%",
        minWidth: 124,
        header: "Sender",
        cell: (sender: MailSender) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-dense-1 text-pb-text">
              {sender.fromAddress}
            </span>
            {sender.fromDisplayName ? (
              <span className="truncate text-dense-2 text-pb-text-muted">
                {sender.fromDisplayName}
              </span>
            ) : null}
          </div>
        ),
      },
      /* ORDER IS THE SPEC: Server | Address | Sender | State | Last tested |
         Actions. State reads before its supporting evidence, not after it. */
      {
        id: "status",
        width: "12%",
        minWidth: 68,
        header: "State",
        cardRole: "badge" as const,
        cell: (sender: MailSender) => (
          <StatusPill
            tone={
              sender.status === "active"
                ? "healthy"
                : sender.status === "draft"
                  ? "watch"
                  : sender.status === "archived"
                    ? "neutral"
                    : "neutral"
            }
          >
            {sender.status === "active"
              ? "Active"
              : sender.status === "draft"
                ? "Draft"
                : sender.status === "archived"
                  ? "Archived"
                  : sender.status === "superseded"
                    ? "Previous"
                    : "Turned off"}
          </StatusPill>
        ),
      },
      {
        id: "test",
        width: "15%",
        minWidth: 85,
        header: (
          <AdminTimeZoneLabel label="Last tested" formatter={formatter} />
        ),
        cell: (sender: MailSender) => (
          /* Outcome AND when. "Last tested" that shows only a verdict answers
             a different question than the one its header asks, and staleness is
             the thing an administrator is actually judging — a pass from two
             months ago is not reassurance. */
          <div className="flex flex-col items-start gap-1">
            {/* PB-5169. The verdict OPENS the detail once there is one to open.
                It replaces the pill rather than sitting beside it: this column
                floors at 85px, so a second control would not fit, and `Failed`
                with nothing behind it was the dead end the ticket describes.
                `as="button"` is the design system's own trigger form, so the
                focus ring and press step come with it. A server that has never
                been tested keeps the plain pill — there is nothing to show. */}
            {sender.lastTestOutcome === null ? (
              <StatusPill tone={testTone(sender.lastTestOutcome)}>
                {testLabel(sender.lastTestOutcome)}
              </StatusPill>
            ) : (
              <StatusPill
                as="button"
                tone={testTone(sender.lastTestOutcome)}
                aria-label={`Last test details for ${sender.resolvedDisplayName}`}
                onClick={() => onOpenTestDetail(sender)}
              >
                {testLabel(sender.lastTestOutcome)}
              </StatusPill>
            )}
            {sender.lastTestAt ? (
              <span className="text-dense-2 text-pb-text-muted">
                {dateTime(sender.lastTestAt)}
              </span>
            ) : null}
            {/* PRESERVED from the collapsed sections this table replaced. A
                retired server's last test is not the useful fact about it —
                when it last actually sent is, because that is what an
                administrator is attributing old mail to. Shown only for the
                two retired states, where it is the question being asked. */}
            {sender.status === "superseded" || sender.status === "archived" ? (
              <span className="text-dense-2 text-pb-text-muted">
                {`Last used: ${formatLastProductionUse(sender, dateTimeOr)}`}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: "actions",
        /* Wide enough for the promoted button AND the menu beside it. This was
           64 — the width from when the column held the menu alone — so once
           Send test was promoted into it the label wrapped onto two lines in a
           fixed-layout table. */
        width: 168,
        minWidth: 168,
        header: <span className="sr-only">Actions</span>,
        align: "end" as const,
        cardRole: "actions" as const,
        cell: (sender: MailSender) => {
          const busyReason =
            runTest.isPending ||
            activate.isPending ||
            enable.isPending ||
            archive.isPending ||
            discard.isPending ||
            disable.isPending
              ? "Another mail-server action is in progress."
              : undefined;
          const items =
            /* ARCHIVED first, because it is the branch with the fewest
               affordances and the one whose home disappeared: Restore used to
               be a button inside the collapsed "Archived servers" section, and
               that section is gone. It lives in the row menu now, where every
               other per-server action already lives.
             *
               A superseded server gets an empty menu on purpose — it can
               neither send nor be restored, which the ticket states outright,
               so an ActionMenu with nothing in it is the honest rendering. */
            sender.status === "archived"
              ? [
                  {
                    id: "restore",
                    label: "Restore",
                    ...(busyReason ? { disabledReason: busyReason } : {}),
                    onSelect: () => {
                      setActionError(null);
                      setConfirmRestore(sender);
                    },
                  },
                ]
              : sender.status === "active"
                ? [
                    {
                      id: "disable",
                      label: "Turn off sending",
                      tone: "critical" as const,
                      ...(busyReason ? { disabledReason: busyReason } : {}),
                      onSelect: () => {
                        setActionError(null);
                        setConfirmDisable(true);
                      },
                    },
                  ]
                : sender.status === "draft"
                  ? [
                      {
                        id: "edit",
                        label: "Edit configuration",
                        ...(busyReason ? { disabledReason: busyReason } : {}),
                        onSelect: () => beginEdit(sender),
                      },
                      {
                        id: "test",
                        label: "Send test message",
                        ...(busyReason ? { disabledReason: busyReason } : {}),
                        onSelect: () => beginTest(sender),
                      },
                      {
                        id: "activate",
                        label: active ? "Activate and replace" : "Activate",
                        ...(!sender.canActivate
                          ? {
                              disabledReason:
                                sender.lastTestReason ??
                                "A passing test message is required first.",
                            }
                          : busyReason
                            ? { disabledReason: busyReason }
                            : {}),
                        onSelect: () => {
                          setActionError(null);
                          setConfirmSwitch({ sender, kind: "activate" });
                        },
                      },
                      {
                        id: "discard",
                        label: "Discard draft",
                        tone: "critical" as const,
                        separatorBefore: true,
                        ...(busyReason ? { disabledReason: busyReason } : {}),
                        onSelect: () => {
                          setActionError(null);
                          setConfirmDiscardId(sender.id);
                        },
                      },
                    ]
                  : [
                      {
                        id: "test",
                        label: "Send test message",
                        ...(busyReason ? { disabledReason: busyReason } : {}),
                        onSelect: () => beginTest(sender),
                      },
                      {
                        id: "enable",
                        label: "Enable and switch",
                        ...(!sender.canEnable
                          ? {
                              disabledReason:
                                sender.lastTestReason ??
                                "A fresh passing test message is required first.",
                            }
                          : busyReason
                            ? { disabledReason: busyReason }
                            : {}),
                        onSelect: () => {
                          setActionError(null);
                          setConfirmSwitch({ sender, kind: "enable" });
                        },
                      },
                      {
                        id: "archive",
                        label: "Archive",
                        tone: "critical" as const,
                        separatorBefore: true,
                        ...(busyReason ? { disabledReason: busyReason } : {}),
                        onSelect: () => {
                          setActionError(null);
                          setConfirmArchive(sender);
                        },
                      },
                    ];
          return (
            /* ONE promoted action beside the menu, which is exactly what AC6
               allows ("promote at most one frequent next action") and what the
               approved design shows. Send test is the right one to promote:
               every lifecycle transition on this page is gated on a fresh
               passing test, so it is the action an administrator reaches for
               most and the one that unblocks the others.
             *
               A superseded sender cannot be tested, so it gets the menu alone
               rather than a button that would only ever refuse. */
            <div className="flex items-center justify-end gap-1">
              {sender.status === "superseded" ? null : (
                <Button
                  variant="secondary"
                  onClick={() => beginTest(sender)}
                  disabled={Boolean(busyReason)}
                  /* The same accessible-name rule the menu items follow: the
                     visible text starts the name and the address disambiguates
                     it, because every row offers an identical label. */
                  aria-label={actionName("Send test", sender)}
                  data-testid={`send-test-${sender.id}`}
                  className="whitespace-nowrap"
                >
                  Send test
                </Button>
              )}
              <ActionMenu
                items={items}
                triggerLabel={actionName("Actions", sender)}
                triggerTestId={`sender-actions-${sender.id}`}
              />
            </div>
          );
        },
      },
    ],
    [
      active,
      activate.isPending,
      archive.isPending,
      disable.isPending,
      discard.isPending,
      enable.isPending,
      runTest.isPending,
      /* The two formatters this memo actually calls — `dateTime` on Last tested
         and `dateTimeOr` through `formatLastProductionUse`. Omitting them froze
         this table on whatever locale and timezone were resolved at first
         paint, while Delivery history (which does list its formatter) moved to
         the reader's preferences the moment they loaded. Two tables on one page
         stamping the same instant differently. */
      dateTime,
      dateTimeOr,
      /* And the formatter itself, which the Last tested header names the zone
         from. It is memoised on the zone, so this changes exactly when the
         rendered times do. */
      formatter,
      onOpenTestDetail,
    ],
  );

  return (
    <AdminSection
      icon={Mail}
      title="Email & notifications"
      description="Manage tenant-wide email delivery, notification events, templates, and delivery activity."
      action={
        /* The shared `action` slot: a read-only sending-state summary and the
           primary "Add mail server" CTA. MembersAdmin puts two controls here
           the same way, which is the layout AC1 says to match — and the CTA
           belongs here rather than under the table, where it used to sit.
         *
           This said "the tenant-wide Send emails Switch (addendum AC21)" and
           described a control that is no longer in this slot: workspace email
           on/off became a module decision and moved to Admin > Modules. The
           addendum is satisfied, in one place rather than two. */
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* READ-ONLY here, and the switch that used to sit beside it is gone.
            *
              Workspace email on/off is a module decision now and lives in one
              place — Admin > Modules > Workspace capabilities — so this page no
              longer offers a second way to set it. What it still reports is
              READINESS, which is a different fact and the one this page owns:
              whether a tested, active mail server exists. The two were easy to
              confuse while both were switches on the same screen.
            *
              The pointer is not decoration: an administrator who used to flip
              this needs to be told where it went, and the module gate is the
              only reason this page is reachable at all. */}
          {capabilities.data?.sendingControl.enabled ? (
            <div className="flex items-center gap-3 rounded-md border border-pb-border bg-pb-card-elevated px-3 py-2">
              <div className="flex flex-col items-start gap-0.5 text-left text-dense-2">
                <span className="text-dense-1 font-medium text-pb-text">
                  Sending state
                </span>
                <StatusPill
                  tone={
                    emailReach === "unknown"
                      ? "neutral"
                      : emailReach === "on"
                        ? "healthy"
                        : "watch"
                  }
                >
                  {/* The KNOWN answer wins, and only an unknown one is described
                      by how it failed. Checking the error first inverted that: a
                      workspace with no active server is certainly paused whatever
                      the settings read did, but a failed settings read relabelled
                      it "Unavailable" here while the tile and the notification
                      rows still said "Sending paused" — two answers on one page,
                      and this pill's own tone still came from `emailReach`, so the
                      text and the colour disagreed too.
                    *
                      Within unknown, a failed read is still named before an
                      unfinished one: "Unavailable" says something is wrong where
                      "Checking..." would say the page is still working. */}
                  {emailReach === "on"
                    ? "Sending on"
                    : emailReach === "paused"
                      ? "Sending paused"
                      : operational.isError ||
                          sendingSettings.isError ||
                          capabilities.isError
                        ? "Unavailable"
                        : "Checking..."}
                </StatusPill>
                {/* The design-system Link on the router (d2); it takes the
                    column's text-dense-2, the label and pill carry their own. */}
                <Link asChild variant="muted">
                  <RouterLink
                    to={ADMIN_SECTION_ROUTES.modules ?? "#"}
                    data-testid="mail-sending-module-link"
                  >
                    Turn email off in Modules
                  </RouterLink>
                </Link>
              </div>
            </div>
          ) : null}
          <Button
            variant="primary"
            className="gap-1.5"
            onClick={beginCreate}
            data-testid="add-mail-server"
          >
            <Plus className="size-4" aria-hidden="true" />
            Add mail server
          </Button>
        </div>
      }
      className="pb-24 sm:pb-0"
      data-testid="communication-admin"
      meta={
        <>
          {/* From the sender read the Mail server tab already makes (d4): who is
              sending now and how the last test went. Nothing here is a total.
            *
              Gated on `activeServerKnown`, the same predicate the status readers
              use, because this card's first row IS "is there an active sender".
              Its own copy of the condition had the same hole they did: an offline
              first read is pending-but-not-fetching, so it rendered "Active
              sender: None" about an inventory nobody had read — beside a tile
              that correctly said the state was unknown. */}
          {activeServerKnown ? (
            <SettingsMetaCard
              icon={Mail}
              title="Delivery"
              rows={[
                {
                  label: "Active sender",
                  value: active ? active.resolvedDisplayName : "None",
                },
                {
                  label: "Last test",
                  value: active?.lastTestAt ? (
                    <>
                      <AdminTimeZoneLabel
                        label={dateTime(active.lastTestAt)}
                        formatter={formatter}
                        variant="inline"
                      />
                      {active.lastTestOutcome
                        ? ` · ${active.lastTestOutcome}`
                        : ""}
                    </>
                  ) : (
                    "Not yet tested"
                  ),
                },
              ]}
            />
          ) : null}
          <AdminAboutCard title="About email">
            {PRODUCT_NAME} sends invitations, access notices and exports through
            the active mail server. Nothing goes out until a sender has been
            tested and activated on the Mail server tab.
          </AdminAboutCard>
          <AdminRelatedCard ids={["members", "readiness"]} />
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Suppressed while EITHER confirmation is open, because both dialogs
          render the same error themselves. Showing both puts the identical
          sentence on screen twice — once under a modal the administrator cannot
          see past — which reads as two separate failures. The discard dialog
          was added by PB-5155 and needs the same suppression as the disable one
          for the same reason. */}
        {actionError &&
        !confirmDisable &&
        confirmDiscardId === null &&
        confirmSwitch === null &&
        confirmArchive === null &&
        confirmRestore === null ? (
          <Alert tone="critical" title="That did not work">
            {actionError}
          </Alert>
        ) : null}

        {/* OUTSIDE the tabs, deliberately.

          This is the state that matters most on the whole page, and the failure it
          prevents is an administrator assuming mail is going out when nothing is
          configured. Behind a tab it would be visible only to someone who already went
          looking, which is precisely the person who does not need telling. */}
        {/* `activeServerKnown`, the SAME predicate the pill and the tile use, and
            paired with `operational` for the same reason `active` is: the
            filtered `senders` query can be a different bucket entirely. This band
            carried its own copy of the condition and so kept the bug they were
            just fixed for — an offline first read is pending-but-not-fetching, so
            every guard here passed and the band asserted "no mail server is
            active" about an inventory nobody had read. */}
        {activeServerKnown && !active ? (
          <Alert tone="watch" title="No mail server is active">
            {PRODUCT_NAME} is not sending email. Invitations, access-granted
            notices and exports have nowhere to go until a mail server is
            configured, tested and activated on the Mail server tab — and
            sending stays paused until one is.
          </Alert>
        ) : null}

        {senders.isError ? (
          <Alert tone="critical" title="Could not load the mail configuration">
            {errorCopy(senders.error)}
          </Alert>
        ) : null}

        {sendingSettings.isError ? (
          <Alert tone="critical" title="Could not load email sending state">
            {errorCopy(sendingSettings.error)}
          </Alert>
        ) : null}
        {/* The state that had no band at all, and the one an administrator is
            least likely to work out: a tested, active mail server, and nothing
            going out because the workspace switch is off. The small pill beside
            the switch was the only signal.
          *
            This replaces an info band on `!active && paused` — a condition that
            was a strict subset of the warning above, so that band only ever
            appeared stacked under it, saying a second time what it had just
            said. Its one unique sentence moved into that warning. */}
        {/* `emailReach`, not the raw settings value: a failed refetch keeps the
            last successful `sendingEnabled: false` in `data`, and this band
            would then assert a pause on the strength of a read that failed —
            while the pill, the tile and the notification rows all said the
            state was not known. `active &&` still stands, because this band is
            specifically about a CONFIGURED server that is not sending. */}
        {active && emailReach === "paused" ? (
          <Alert tone="watch" title="Workspace email is paused">
            {/* "Turn Send emails on" named the header switch, which no longer
                exists — a dead instruction is worse than none. With that switch
                gone the sending state is derived from sender readiness, so the
                only way to reach this banner is an explicit pause written
                outside the admin console. Say that, rather than inventing an
                action the reader cannot take. */}
            {active.resolvedDisplayName} is configured and tested, but the
            workspace sending state is off. The admin console no longer sets
            this directly — to stop or resume email, use Email under Modules; if
            that is already on, ask your operator to clear the pause.
          </Alert>
        ) : null}

        {/* Four concerns that were four stacked sections. Each is read on its own — an
          administrator configuring a sender is not simultaneously auditing what was
          sent — so stacking them only ever cost scrolling. Reuses packages/ui Tabs
          (APG roles, roving tabindex, aria-controls) rather than hand-rolling a
          tablist, matching MembersAdmin. */}
        <Tabs
          /* Four tabs do not fit a phone-width lane on one line; they wrap rather
             than push the lane sideways, as GenAI configuration\'s tabs do. */
          className="min-w-0 [&>[role=tablist]]:flex-wrap"
          aria-label="Email and notification settings"
          items={[
            /* Labels are the ticket's, verbatim. The short forms this page
               shipped with ("Notifications", "Messages") are what let the
               approved design and the build drift apart unnoticed. */
            { value: "server", label: "Mail server" },
            { value: "notifications", label: "Notification events" },
            { value: "messages", label: "Message templates" },
            { value: "history", label: "Delivery history" },
          ]}
          /* CONTROLLED, so a row action can move the administrator to the tab
             that answers it. Uncontrolled tabs would have left the Notification
             events Actions menu with nothing real to offer, and an ActionMenu
             whose only item is inert is worse than no column. */
          value={tab}
          /* Clearing the carrier on the way out. It is set by a Notification
             events row action and read when Delivery history mounts, so
             leaving it set meant the NEXT visit to that tab — by the tab strip,
             from anywhere — silently reapplied the previous row's event filter
             with nothing on screen explaining why the list was narrowed.
           *
             This line is ALSO what makes the same row action work a second
             time, which is worth stating because review has read it the other
             way three times. The trigger lives in the Notification events
             panel, so getting back to it runs this reset: the carrier goes
             null -> "E" on the repeat rather than "E" -> "E", and the seeding
             effect's dependency fires even when the administrator cleared the
             filters by hand in between. The panel unmounting is a second,
             independent guarantee (`keepMounted` defaults off, so the effect
             re-runs on mount) — which is why a nonce would be dead state. Turn
             `keepMounted` on for the history panel and this line becomes the
             only guarantee left. */
          onValueChange={(next) => {
            if (next !== "history") setHistoryEvent(null);
            setTab(next);
          }}
        >
          <TabPanel value="server" data-testid="mail-server-panel">
            {senders.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : senders.isError ? null : (
              <div className="flex flex-col gap-4">
                {/* The compact health summary the ticket asks for, with all
                    four facts: Sending state, Active server, Last connection
                    test and Sender-domain health. Two of them used to live in
                    the side rail, which put the page's most important status
                    beside it rather than in it.
                  *
                    Every tile carries its own words — a tone alone never
                    communicates a state (AC3). */}
                <MailServerHealthSummary
                  emailReach={emailReach}
                  active={active ?? null}
                  activeServerKnown={activeServerKnown}
                  onReviewRecords={() => setRecordsOpen(true)}
                />
                <div className="flex flex-wrap items-end gap-3 rounded-xl border border-pb-border bg-pb-card-elevated/25 p-3">
                  <div className="w-full md:min-w-64 md:flex-[2]">
                    <Input
                      label="Search mail servers"
                      value={senderKeyword}
                      onChange={(event) => {
                        setSenderKeyword(event.target.value);
                        setSenderPage(1);
                      }}
                      placeholder="Name, address or host"
                      prefix={
                        <Search className="size-3.5" aria-hidden="true" />
                      }
                      data-testid="sender-keyword-filter"
                    />
                  </div>
                  <div
                    className="w-full sm:w-44"
                    data-testid="sender-status-filter"
                  >
                    <Select
                      label="State"
                      value={senderStatus}
                      /* The ticket's five options. "Draft" is kept as a sixth
                         because this page really does have draft senders and
                         hiding the filter for a state the table displays would
                         make them unreachable — the spec omits it rather than
                         forbidding it. */
                      options={[
                        { value: "", label: "All states" },
                        { value: "active", label: "Active" },
                        { value: "draft", label: "Draft" },
                        { value: "disabled", label: "Turned off" },
                        { value: "test_failed", label: "Test failed" },
                        { value: "archived", label: "Archived" },
                        { value: "superseded", label: "Previous" },
                      ]}
                      onChange={(value) => {
                        setSenderStatus(value);
                        setSenderPage(1);
                      }}
                      data-testid="sender-status-filter"
                    />
                  </div>
                  <p className="w-full text-dense-1 text-pb-text-muted @min-[58.5rem]/settings-page:ms-auto @min-[58.5rem]/settings-page:w-auto @min-[58.5rem]/settings-page:pb-2.5">
                    Showing {filteredSenders.length} of {rows.length} servers
                  </p>
                </div>
                <FilterBar
                  filters={[
                    ...(senderKeyword.trim()
                      ? [
                          {
                            id: "keyword",
                            label: "Search",
                            value: senderKeyword.trim(),
                          },
                        ]
                      : []),
                    ...(senderStatus
                      ? [
                          {
                            id: "status",
                            label: "State",
                            /* Read off the same option list the Select
                               renders, so a new state cannot show the chip
                               "Turned off" for something else — which is what
                               the previous fallback-to-"Turned off" ternary
                               would have done to both new options. */
                            value:
                              SENDER_STATE_LABELS[senderStatus] ?? senderStatus,
                          },
                        ]
                      : []),
                  ]}
                  onRemove={(id) => {
                    if (id === "keyword") setSenderKeyword("");
                    if (id === "status") setSenderStatus("");
                    setSenderPage(1);
                  }}
                  onClearAll={clearSenderFilters}
                />
                <DataTable
                  layout="fixed"
                  columns={senderColumns}
                  data={visibleSenders}
                  getRowId={(sender) => sender.id}
                  /* Until the read SUCCEEDS there is no empty list to describe,
                     only an unread one. Without this the table answered an
                     offline paused read with "No mail server configured", which
                     is a claim about data it never received; `loading` suppresses
                     the empty state and shows skeleton rows instead, leaving the
                     health tile above it free to say the state is unknown. */
                  loading={!senders.isSuccess}
                  responsive
                  pinFirstColumn
                  textSize="small"
                  caption="Configured mail servers"
                  emptyState={
                    senderKeyword.trim() || senderStatus ? (
                      <EmptyState
                        variant="no-results"
                        title="No mail servers match"
                        description="Try a different search or clear the filters."
                        action={
                          <Button
                            variant="outline"
                            onClick={clearSenderFilters}
                          >
                            Clear filters
                          </Button>
                        }
                      />
                    ) : (
                      <EmptyState
                        title="No mail server configured"
                        description="Add a server, send a test message and activate it before enabling workspace email."
                      />
                    )
                  }
                />
                {senderPageCount > 1 ? (
                  <div className="flex justify-center">
                    <Pagination
                      mode="numbered"
                      page={currentSenderPage}
                      pageCount={senderPageCount}
                      onChange={setSenderPage}
                    />
                  </div>
                ) : null}

                {/* The "Add mail server" CTA moved to the shared page header,
                    where the ticket puts it and where MembersAdmin puts its
                    own primary action. It is not duplicated here: two buttons
                    with one testid would also make the e2e selector
                    ambiguous. */}

                {/* PB-2938's records panel, on demand. Still inside the server
                    tab, beside the sender whose from-domain it diagnoses — the
                    records are a property of that sender's domain, not a
                    separate concern an administrator goes looking for. */}
                <Modal
                  open={recordsOpen}
                  onClose={() => setRecordsOpen(false)}
                  title="Sender records"
                  description="How receiving servers judge mail from your sending domain. Checked only for domains this workspace has verified."
                >
                  <SenderRecordsPanel />
                </Modal>

                {/* The two collapsed "Previous servers" / "Archived servers"
                    sections that used to sit here are GONE. They were states of
                    a mail server rendered as a third and fourth list UI below
                    the table, and between them they owned more vertical space
                    than the table itself. Both are now reachable from the State
                    filter, which loads the matching group on demand. Restore
                    moved into the row ActionMenu, where every other per-server
                    action already lives. */}
              </div>
            )}
          </TabPanel>

          <TabPanel value="notifications">
            <NotificationPolicySection
              /* TWO values, because the section asks two different questions of
                 one prop. Editability is the administrator's SETTING: policies
                 must stay configurable before a server is ever activated, which
                 is exactly the window a workspace is set up in. Row status is
                 what is actually going out, which a missing server settles
                 whatever the setting says — without it the rows read a healthy
                 "Email and in-app" while the header said "Sending paused".
               *
                 The capability fallback is unchanged: a deployment with no
                 sending control has no pause to report, only a missing server. */
              emailSendingEnabled={
                capabilities.data?.sendingControl.enabled === true
                  ? (sendingSettings.data?.sendingEnabled ?? true)
                  : true
              }
              emailReach={emailReach}
              onViewHistory={(event) => {
                /* Carries the event across rather than dumping the
                   administrator on an unfiltered list: the message filter is
                   what makes "this event's history" true instead of merely
                   "the history tab". */
                setHistoryEvent(event.typeName);
                setTab("history");
              }}
            />
          </TabPanel>

          <TabPanel value="messages">
            <MessageCatalogue />
          </TabPanel>

          <TabPanel value="history">
            <DeliveryResults event={historyEvent} />
          </TabPanel>
        </Tabs>

        {/* The pause confirmation that used to live here is gone with the
            switch that opened it. Its blast-radius warning moved to the
            Modules page's own disable dialog, which is now the only way to
            turn workspace email off. */}

        <Modal
          open={editorMode !== null}
          onClose={requestEditorClose}
          title={
            editorMode === "edit"
              ? "Edit mail server"
              : active
                ? "Add replacement mail server"
                : "Add mail server"
          }
          description={`${PRODUCT_NAME} stores the vault reference, never the password.`}
          size="lg"
        >
          {editorMode === "edit" ? (
            <AddSenderForm
              form={editForm}
              setForm={setEditForm}
              fieldErrors={editFieldErrors}
              onFieldEdited={forgetFieldError(setEditFieldErrors)}
              error={editError}
              submitting={saveEdit.isPending}
              onSubmit={submitEdit}
              submitLabel="Save changes"
              notice="Saving changes clears the last test result. Send a new test message before activating."
              formTestId="edit-sender-form"
              onCancel={requestEditorClose}
              secretExamples={capabilities.data?.secretReferences.examples}
            />
          ) : editorMode === "create" ? (
            <AddSenderForm
              form={form}
              setForm={setForm}
              fieldErrors={fieldErrors}
              onFieldEdited={forgetFieldError(setFieldErrors)}
              error={formError}
              submitting={create.isPending}
              onSubmit={submit}
              onCancel={requestEditorClose}
              secretExamples={capabilities.data?.secretReferences.examples}
            />
          ) : null}
        </Modal>

        <DiscardChangesDialog
          open={confirmEditorDiscard}
          onKeepEditing={() => setConfirmEditorDiscard(false)}
          onDiscard={discardEditorChanges}
          description="The mail server details you changed are not saved."
        />

        <Modal
          open={testTarget !== null}
          onClose={() => {
            if (!runTest.isPending) setTestTarget(null);
          }}
          title={
            testTarget
              ? `Test ${testTarget.resolvedDisplayName}`
              : "Test mail server"
          }
          description="The test is sent to your signed-in email address."
          size="sm"
          footer={
            <>
              {(testError || testResult?.lastTestOutcome === "failed") &&
              testTarget ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setTestResult(null);
                    setTestError(null);
                    runTest.mutate(testTarget.id);
                  }}
                  disabled={runTest.isPending}
                >
                  Try again
                </Button>
              ) : null}
              <Button
                variant="secondary"
                onClick={() => setTestTarget(null)}
                disabled={runTest.isPending}
              >
                Close
              </Button>
            </>
          }
        >
          {runTest.isPending ? (
            <Alert tone="info" title="Sending test message…">
              Keep this window open while {PRODUCT_NAME} contacts the mail
              server.
            </Alert>
          ) : testError ? (
            <Alert tone="critical" title="The test could not run">
              {testError} Check the configuration, then try again.
            </Alert>
          ) : testResult?.lastTestOutcome === "passed" ? (
            <Alert tone="healthy" title="Mail server accepted the test">
              The SMTP server accepted the message. This does not confirm that
              it reached the inbox; check the signed-in account before turning
              sending on.
            </Alert>
          ) : testResult?.lastTestOutcome === "failed" ? (
            <Alert tone="critical" title="Test message failed">
              {testResult.lastTestReason ?? "The mail server refused the test."}{" "}
              A test is never retried automatically; correct the problem and try
              again.
            </Alert>
          ) : null}
        </Modal>

        {/* ConfirmDialog (d2): nothing is lost, so the neutral tone. */}
        <ConfirmDialog
          open={confirmSwitch !== null}
          onClose={() => setConfirmSwitch(null)}
          title={
            confirmSwitch?.kind === "enable"
              ? "Turn sending back on?"
              : active
                ? "Activate and replace?"
                : "Activate this server?"
          }
          tone="neutral"
          confirmLabel={
            confirmSwitch?.kind === "enable"
              ? "Turn sending back on"
              : "Activate server"
          }
          loading={activate.isPending || enable.isPending}
          onConfirm={() => {
            if (!confirmSwitch) return;
            setActionError(null);
            if (confirmSwitch.kind === "activate") {
              activate.mutate({
                senderId: confirmSwitch.sender.id,
                supersedesId: active?.id ?? null,
              });
            } else {
              enable.mutate({
                senderId: confirmSwitch.sender.id,
                switchesFromId: active?.id ?? null,
              });
            }
          }}
        >
          <div className="flex flex-col gap-4">
            <p className="text-pb-text">
              {active
                ? `${active.resolvedDisplayName} is active now. ${PRODUCT_NAME} will switch directly to ${confirmSwitch?.sender.resolvedDisplayName ?? "the selected server"}; there is no moment with two active servers or no active server.`
                : `This server will become the only active server and ${PRODUCT_NAME} can start sending email through it.`}
            </p>
            {actionError ? <Alert tone="critical">{actionError}</Alert> : null}
          </div>
        </ConfirmDialog>

        {/* ConfirmDialog (d2): history stays and it can be restored, so the
            neutral tone. */}
        <ConfirmDialog
          open={confirmArchive !== null}
          onClose={() => setConfirmArchive(null)}
          title="Archive this server?"
          tone="neutral"
          confirmLabel="Archive server"
          loading={archive.isPending}
          onConfirm={() => {
            setActionError(null);
            if (confirmArchive) archive.mutate(confirmArchive.id);
          }}
        >
          <div className="flex flex-col gap-4">
            <p className="text-pb-text">
              It moves out of the everyday list. Delivery and audit history stay
              available, and it can be restored later as a turned-off server.
            </p>
            {actionError ? <Alert tone="critical">{actionError}</Alert> : null}
          </div>
        </ConfirmDialog>

        <ConfirmDialog
          open={confirmRestore !== null}
          onClose={() => setConfirmRestore(null)}
          title="Restore this server?"
          tone="neutral"
          confirmLabel="Restore server"
          loading={restore.isPending}
          onConfirm={() => {
            setActionError(null);
            if (confirmRestore) restore.mutate(confirmRestore.id);
          }}
        >
          <div className="flex flex-col gap-4">
            <p className="text-pb-text">
              Restore returns it as turned off. It will not send until a fresh
              test passes and you explicitly turn sending back on.
            </p>
            {actionError ? <Alert tone="critical">{actionError}</Alert> : null}
          </div>
        </ConfirmDialog>

        {/* Outside the tabs: a confirmation that must survive whichever panel is showing,
          and whose trigger lives on the Mail server tab. */}
        {/* ConfirmDialog (d2): the destructive confirm, red chip, quiet Cancel. */}
        <ConfirmDialog
          open={confirmDisable}
          /* Backing out is not a failure. Leaving the banner up told the
             administrator something went wrong with a decision they had just
             abandoned. */
          onClose={() => {
            setActionError(null);
            setConfirmDisable(false);
          }}
          title="Turn off sending?"
          confirmLabel="Turn off sending"
          loading={disable.isPending}
          onConfirm={() => {
            /* Cleared before the request, not only after it: otherwise the
               last rejection sits above a retry for its whole duration. */
            setActionError(null);
            if (active) disable.mutate(active.id);
          }}
        >
          <div className="flex flex-col gap-4">
            <p className="text-pb-text">
              {PRODUCT_NAME} will stop sending email entirely — invitations,
              access-granted notices and export links included. You can turn
              this server back on later, but it will have to pass a test send
              first.
            </p>
            {/* Inside the dialog, matching the invite-revoke precedent: a failed
              disable leaves this open, and an error rendered in the card behind
              would be covered by it — leaving an admin with a button that looks
              inert. Two tabs, where the second gets a 409, reaches this.

              `Alert` rather than the bare red paragraph this used to be: it
              already carries `role="alert"`, so the announcement is unchanged,
              and it stops the dialogs rendering a failure in a style nothing
              else on the page uses. */}
            {actionError ? (
              <Alert tone="critical" title="That did not work">
                {actionError}
              </Alert>
            ) : null}
          </div>
        </ConfirmDialog>

        {/* CONFIRMED, but without a type-the-name step. There is deliberately no
          un-discard, and a misclick on a draft whose test has just passed costs
          a full retype of eight fields — so the click is worth confirming. The
          friction is sized for a configuration that never sent mail, though,
          not for production infrastructure: naming it back would be theatre. */}
        <ConfirmDialog
          open={confirmDiscardId !== null}
          onClose={() => {
            setActionError(null);
            setConfirmDiscardId(null);
          }}
          title="Discard this configuration?"
          confirmLabel="Discard configuration"
          loading={discard.isPending}
          onConfirm={() => {
            setActionError(null);
            if (confirmDiscardId) discard.mutate(confirmDiscardId);
          }}
        >
          <div className="flex flex-col gap-4">
            <p className="text-pb-text">
              It has never sent mail, so nothing stops going out. It will not
              appear here again, and there is no way to bring it back — you
              would enter the eight fields afresh.
            </p>
            {/* Where it went, asked and answered in the one place it can be:
              this dialog. The tab itself must not carry a group, a count or a
              hint for configurations thrown away — a count discloses exactly
              what is meant to be gone — but "it vanished and I cannot tell
              whether anything kept a record" is a fair question, and the
              record does exist. */}
            <p className="text-pb-text-muted">
              The tenant audit history keeps a record of it being thrown away,
              and of who did it.
            </p>
            {/* Same reasoning as the disable dialog: a failed discard leaves this
              open, and an error behind it would be covered. */}
            {actionError ? (
              <Alert tone="critical" title="That did not work">
                {actionError}
              </Alert>
            ) : null}
          </div>
        </ConfirmDialog>

        <SenderTestDetail
          sender={testDetail}
          onClose={() => setTestDetail(null)}
        />
      </div>
    </AdminSection>
  );
}

/* A handful. With the search bounded to a one-millisecond window the answer is
   one row, so this is headroom rather than a page size. */
const SENDER_TEST_LOOKUP_LIMIT = 5;

/** The instant one millisecond after `at`, or null. The upper bound of the
 *  lookup window — see where it is used for why it cannot be `at` itself. */
function millisecondAfter(at: string | null): string | null {
  if (at === null) return null;
  const parsed = Date.parse(at);
  return Number.isNaN(parsed) ? null : new Date(parsed + 1).toISOString();
}

/**
 * Why a server's last test ended the way it did, and whether another attempt is
 * worth making (PB-5169).
 *
 * Two sources, deliberately. The OUTCOME, the time and the reason come from the
 * sender row, which is the record the lifecycle itself keeps. The NEXT STEP
 * comes from the connection test's delivery row, because the retry verdict is
 * stored there and nowhere else — `eventType: null` is the contract's own
 * "connection tests" filter, so this asks for that server's tests rather than
 * its mail.
 *
 * The freshness check is not defensive tidiness. `disable` clears the sender's
 * test triple but leaves the delivery row alive, so the newest stored test can
 * outlive the sender's own record of it. Reading a next step off that row would
 * answer for a test this server no longer claims to have had.
 */
function SenderTestDetail({
  sender,
  onClose,
}: {
  sender: MailSender | null;
  onClose: () => void;
}): ReactElement {
  const formatter = useDateTimeFormatter();
  /* Guidance exists only for a failure, so the lookup only runs for one. A
     passed test needs no next step and must not cost a request. */
  const needsGuidance = sender?.lastTestOutcome === "failed";
  const test = useQuery({
    queryKey: [
      ...mailDeliveriesQueryKey,
      "sender-test",
      sender?.id ?? null,
      sender?.lastTestAt ?? null,
    ],
    enabled: sender !== null && needsGuidance,
    queryFn: () =>
      searchMailDeliveries({
        senderId: sender?.id ?? null,
        /* SETTLED failures only. `claimTestDelivery` commits a `pending` row
           before SMTP runs and the lifecycle allows it to stay orphaned, so a
           newer attempt that never finished would otherwise sort first and
           answer in place of the test this sender actually recorded. */
        outcome: "rejected",
        eventType: null,
        /* Bounded to the recorded instant, so the row cannot be pushed out of
           the page by newer attempts.
         *
           The bounds are a ONE-MILLISECOND window, not an equality. The column
           is `timestamptz` with microsecond precision, but the driver parses it
           into a JS `Date` and the wire carries milliseconds — so the value
           here is the FLOOR of the stored one (`...322Z` for a row at
           `...322814`). Asking the server for `submitted_at BETWEEN x AND x`
           therefore matches nothing at all: measured against this database, an
           exact-bound query on a real test row returns false. The floor is a
           safe LOWER bound and floor + 1ms a safe upper one, and the exact
           match below still decides which row it is. */
        submittedFrom: sender?.lastTestAt ?? null,
        submittedTo: millisecondAfter(sender?.lastTestAt ?? null),
        recipient: null,
        limit: SENDER_TEST_LOOKUP_LIMIT,
        cursor: null,
      }),
  });

  /* THE row the sender recorded — not the newest one, and never a guess.
   *
     TWO keys, because one is not enough. `settleDelivery` and
     `recordTestResult` run in ONE transaction and both write `now()`, which
     Postgres resolves once per transaction, so the delivery's `submitted_at`
     and the sender's `last_test_at` are the same stored instant. But the wire
     carries MILLISECONDS, so matching on it narrows to a millisecond and no
     further — it is not the exact correlation an earlier revision of this
     comment claimed. Concurrent tests are allowed: each claims its own
     delivery and only one wins the sender's fence, so two rejected attempts
     can settle inside one millisecond.
   *
     The reason separates them. Both writes take the SAME `reason` string, so
     the delivery belonging to this sender's recorded test is the one carrying
     the sender's own `lastTestReason`.
   *
     Two rows matching on both are indistinguishable in everything the wire
     carries. Showing either verdict would be a guess presented as a fact, so
     none is shown and the region says why.
   *
     And uniqueness is only provable on a WHOLE answer. `hasMore` means the
     window holds rows this page did not return, so a second match could be
     sitting on the next one — a single visible match would then look unique
     without being it, which is the same guess wearing a count of 1. A
     truncated answer is therefore treated exactly like a tie. */
  const recordedAt =
    sender?.lastTestAt != null ? Date.parse(sender.lastTestAt) : null;
  const matches = (test.data?.deliveries ?? []).filter(
    (row) =>
      recordedAt !== null &&
      Date.parse(row.submittedAt) === recordedAt &&
      row.failureReason === sender?.lastTestReason,
  );
  const truncated = test.data?.pagination.hasMore === true;
  const ambiguous = truncated || matches.length > 1;
  const recorded = ambiguous ? null : (matches[0] ?? null);

  return (
    <Modal
      open={sender !== null}
      onClose={onClose}
      title="Last test"
      {...(sender ? { description: sender.resolvedDisplayName } : {})}
    >
      {sender ? (
        <DescriptionList data-testid="sender-test-detail">
          <DescriptionItem label="Outcome">
            <StatusPill tone={testTone(sender.lastTestOutcome)}>
              {testLabel(sender.lastTestOutcome)}
            </StatusPill>
          </DescriptionItem>
          <DescriptionItem
            label={
              <AdminTimeZoneLabel
                label="Tested"
                formatter={formatter}
                variant="inline"
              />
            }
          >
            {sender.lastTestAt
              ? formatter.dateTime(sender.lastTestAt)
              : "This server has not been tested."}
          </DescriptionItem>
          {sender.lastTestOutcome === "failed" ? (
            <DescriptionItem label="Reason">
              {sender.lastTestReason ?? "No reason was recorded."}
            </DescriptionItem>
          ) : null}
          {needsGuidance ? (
            <DescriptionItem label="Next step">
              {/* All four states (PB-5221). Silence here would be the worst of
                  them: the modal would look complete while omitting the exact
                  answer this surface exists to give, and a reader could not
                  tell "nothing was recorded" from "the request failed". */}
              <AsyncSection
                size="sm"
                status={asyncStatus({
                  isLoading: test.isLoading,
                  isError: test.isError,
                  isEmpty: test.isSuccess && recorded === null,
                  hasData: recorded !== null,
                })}
                loadingLabel="the next step"
                loadingMessage="Looking up the next step…"
                errorTitle="Couldn't load the next step"
                errorDescription="The delivery history could not be read, so there is no retry guidance to show yet."
                onRetry={() => void test.refetch()}
                empty={
                  <span className="text-pb-text-muted">
                    {ambiguous
                      ? "More than one test finished at that moment, so the next step cannot be matched to this one."
                      : "No retry guidance was recorded for this test."}
                  </span>
                }
              >
                {recorded ? retryGuidance(recorded.failureRetryable) : null}
              </AsyncSection>
            </DescriptionItem>
          ) : null}
        </DescriptionList>
      ) : null}
    </Modal>
  );
}

/**
 * The Mail server tab's compact health summary — four facts, one row.
 *
 * Sender-domain health is a ROLL-UP of SPF, DKIM and DMARC across the verified
 * domains, and the roll-up has to stay honest about two verdicts the record
 * checker treats as non-failures: `not_configured` means nobody supplied a DKIM
 * selector, and `could_not_check` means the resolver was unreachable. Colouring
 * either red sends an administrator to edit DNS that is already correct, so
 * they count as "needs attention" in the wording and never as a failure tone.
 *
 * Deliberately NOT a claim about inbox delivery — the ticket forbids presenting
 * DNS health as guaranteed delivery, so the label says what was checked.
 */
function MailServerHealthSummary({
  emailReach,
  active,
  activeServerKnown,
  onReviewRecords,
}: {
  /** The page's one answer to "is email going out", rendered as-is. */
  emailReach: "on" | "paused" | "unknown";
  active: MailSender | null;
  /** False until every operational sender page has arrived successfully. */
  activeServerKnown: boolean;
  onReviewRecords: () => void;
}): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTime } = formatter;
  /* Same selector and same query key as `SenderRecordsPanel`, so TanStack
     serves both from one fetch rather than issuing a second request for the
     records this tab already loads further down. */
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  const domains = useQuery({
    queryKey: mailDomainStatusQueryKey(tenantId),
    queryFn: getMailDomainStatus,
    enabled: Boolean(tenantId),
  });

  const checked = (domains.data ?? []).filter((entry) => entry.findings);
  const attention = checked.filter((entry) =>
    [
      entry.findings?.spf.verdict,
      entry.findings?.dkim.verdict,
      entry.findings?.dmarc.verdict,
    ].some((verdict) => verdict && verdict !== "pass"),
  ).length;

  const domainHealth = domains.isLoading
    ? { tone: "neutral" as const, text: "Checking..." }
    : domains.isError
      ? { tone: "neutral" as const, text: "Unavailable" }
      : checked.length === 0
        ? { tone: "neutral" as const, text: "No verified domain" }
        : attention === 0
          ? { tone: "healthy" as const, text: `${checked.length} verified` }
          : {
              tone: "watch" as const,
              text: `${checked.length} verified · ${attention} need attention`,
            };

  const tiles = [
    {
      label: "Sending state",
      tone:
        emailReach === "unknown"
          ? ("neutral" as const)
          : emailReach === "on"
            ? ("healthy" as const)
            : ("watch" as const),
      text:
        emailReach === "unknown"
          ? "Unknown"
          : emailReach === "on"
            ? "Sending on"
            : "Sending paused",
    },
    {
      label: "Active server",
      tone: !activeServerKnown
        ? ("neutral" as const)
        : active
          ? ("healthy" as const)
          : ("watch" as const),
      text: !activeServerKnown
        ? "Unknown"
        : active
          ? active.resolvedDisplayName
          : "None",
    },
    {
      label: "Last connection test",
      tone:
        active?.lastTestOutcome === "passed"
          ? ("healthy" as const)
          : active?.lastTestOutcome === "failed"
            ? ("critical" as const)
            : ("neutral" as const),
      text:
        active?.lastTestOutcome === "passed"
          ? "Succeeded"
          : active?.lastTestOutcome === "failed"
            ? "Failed"
            : "Not tested",
      detail: active?.lastTestAt ? (
        <AdminTimeZoneLabel
          label={dateTime(active.lastTestAt)}
          formatter={formatter}
          variant="inline"
        />
      ) : null,
    },
    /* The records detail opens from HERE rather than sitting permanently
       expanded at the foot of the tab, which is where it used to add a third
       screen of scroll for something most visits never read. The tile answers
       the question ("are the records healthy?"); the drawer answers the
       follow-up. Matches the approved design, which links "Review records"
       from the summary rather than inlining the panel. */
    { label: "Sender records", ...domainHealth, review: true },
  ];

  /* Look row OK-68: each fact is a StatTile on the inset card, its status
     pill in the status row; the group is named by the fact's label. */
  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 @min-[58.5rem]/settings-page:grid-cols-4"
      data-testid="mail-health-summary"
    >
      {tiles.map((tile) => (
        <StatTile
          key={tile.label}
          variant="inset"
          role="group"
          aria-label={tile.label}
          label={tile.label}
          statusSlot={
            <>
              <StatusPill tone={tile.tone}>{tile.text}</StatusPill>
              {"detail" in tile && tile.detail ? (
                <span className="text-dense-2 text-pb-text-muted">
                  {tile.detail}
                </span>
              ) : null}
            </>
          }
          actionsSlot={
            "review" in tile && tile.review ? (
              <Button
                variant="ghost"
                onClick={onReviewRecords}
                data-testid="review-sender-records"
              >
                Review records
              </Button>
            ) : null
          }
        />
      ))}
    </div>
  );
}

const POLICY_PAGE_SIZE = 12;

/**
 * Who each MVP event reaches, for the Recipient column.
 *
 * Copy, not data: no layer has a recipient or audience field — the registry
 * carries moduleId, class, severity, ack and href, and `recipient*` elsewhere on
 * this page is per-DELIVERY history, not per-event audience. So this is the
 * frontend's own vocabulary, which is what `notification-event-labels.ts` exists
 * to be for registry names generally.
 *
 * Three entries only, because the tab exposes exactly the three MVP events. A
 * fourth would need a registry field and a product decision about what the
 * audience of an arbitrary module's event even is, so an unknown key renders
 * nothing rather than guessing.
 */
/** The events this MVP page covers, in the order it lists them.
 *
 *  One list, because two surfaces consume it: Notification events renders a row
 *  per entry, and Delivery history offers a filter option per entry. Hand-listing
 *  them twice is how the filter came to omit `invitation` — the row's own "View
 *  delivery history" action then set a value its Select could not display. */
const MVP_EVENT_TYPES = [
  "invitation",
  "identity.member.added",
  "admin.export.ready",
] as const;

/**
 * What will actually happen for one event, as one pill.
 *
 * The workspace pause is the fact the two channel columns cannot carry per row:
 * an event's email switch stays ON while sending is paused, because the pause is
 * not a decision about this event and un-pausing must not silently change it. So
 * the row needs somewhere to say that this email is not currently going out, and
 * this is it.
 */
function eventNotificationState(
  event: NotificationPolicyEvent,
  emailReach: "on" | "paused" | "unknown",
): { label: string; tone: "healthy" | "watch" | "neutral" } {
  /* An undeclared channel is one this event cannot be delivered on, which reads
     the same way as one that is switched off — the same conclusion the channel
     cell reaches when it renders an em dash for a missing decision. */
  const emailOn = channelOf(event, "email")?.enabled === true;
  const inAppOn = channelOf(event, "in_app")?.enabled === true;
  if (emailOn && emailReach === "paused")
    return { label: "Email paused", tone: "watch" };
  /* Neutral, not healthy: the row does not know yet, and green here would be a
     claim. Its own words rather than a blank, because an empty cell reads as
     "nothing to say" where this cell has something to say. */
  if (emailOn && emailReach === "unknown")
    return { label: "Email status unknown", tone: "neutral" };
  if (emailOn)
    return {
      label: inAppOn ? "Email and in-app" : "Email only",
      tone: "healthy",
    };
  if (inAppOn) return { label: "In-app only", tone: "neutral" };
  /* Neither channel carries it, so nobody hears about it at all — a state worth
     its own words rather than a greyed version of "Ready". */
  return { label: "Not notified", tone: "neutral" };
}

const EVENT_RECIPIENTS: Readonly<Record<string, string>> = {
  invitation: "Invited person",
  "identity.member.added": "New member",
  "admin.export.ready": "Admins",
};

/**
 * Per-tenant notification policy: which events notify, and on which channel.
 *
 * The two account-aware MVP events come from the notification type registry. Invitation
 * is an addressed message sent before the recipient has an account, so it is represented
 * explicitly instead of inventing a registry event. Reminder and summary events remain
 * outside this MVP even when another module registers them.
 *
 * Only `switchable` channels render an actual control. `locked` and `unavailable` are
 * shown DIFFERENTLY on purpose: locked means the event always notifies on that channel
 * and is not the tenant's to change, unavailable means the channel cannot carry it at
 * all. Rendering both as one greyed switch would tell an administrator that a message
 * they do receive is one they do not.
 *
 * The server re-checks every one of these rules on the PUT. Hiding a control here is a
 * courtesy, not a control.
 */
function NotificationPolicySection({
  emailSendingEnabled,
  emailReach,
  onViewHistory,
}: {
  /** The workspace SETTING, which decides whether a policy may be EDITED. An
      unresolved read leaves it editable: locking an administrator out of a
      policy because a status request is in flight is the worse failure. */
  emailSendingEnabled: boolean;
  /** Whether email is actually going out, which decides what a row REPORTS.
      Three values, because "not known yet" is not "yes". */
  emailReach: "on" | "paused" | "unknown";
  onViewHistory: (event: NotificationPolicyEvent) => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [actionError, setActionError] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  const [area, setArea] = useState("");
  /** Which (event, channel) is mid-flight, so only that row's control disables. */
  const [pending, setPending] = useState<string | null>(null);

  const policy = useQuery({
    queryKey: notificationPolicyQueryKey,
    queryFn: listNotificationPolicy,
  });

  const events = useMemo<NotificationPolicyEvent[]>(() => {
    const registered = new Map(
      (policy.data ?? []).map((event) => [event.typeName, event]),
    );
    const invitation: NotificationPolicyEvent = {
      typeName: "invitation",
      moduleId: null,
      class: "informational",
      channels: [
        {
          channel: "in_app",
          enabled: false,
          control: "unavailable",
          reason: "The recipient does not have a workspace account yet.",
        },
        {
          channel: "email",
          enabled: true,
          control: "locked",
          reason: "Sent automatically when an invitation is created.",
        },
      ],
    };
    return [
      invitation,
      // Invitation is represented explicitly above; the rest come from the
      // registry, and only if it actually registered them.
      ...MVP_EVENT_TYPES.filter(
        (typeName) => typeName !== "invitation",
      ).flatMap((typeName) => {
        const event = registered.get(typeName);
        return event ? [event] : [];
      }),
    ];
  }, [policy.data]);
  const areaOptions = useMemo(() => {
    const modules = new Map<string, string>();
    for (const event of events) {
      const value = event.moduleId ?? "__platform__";
      modules.set(value, labelForModule(event.moduleId));
    }
    return [
      { value: "", label: "All areas" },
      ...[...modules.entries()]
        .sort((left, right) => left[1].localeCompare(right[1]))
        .map(([value, label]) => ({ value, label })),
    ];
  }, [events]);
  const filteredEvents = useMemo(() => {
    const term = keyword.trim().toLocaleLowerCase();
    return events.filter((event) => {
      const label = labelForNotificationType(event.typeName);
      const matchesKeyword =
        term === "" ||
        [label.title, label.description, event.typeName].some((value) =>
          value?.toLocaleLowerCase().includes(term),
        );
      const matchesArea =
        area === "" || (event.moduleId ?? "__platform__") === area;
      return matchesKeyword && matchesArea;
    });
  }, [area, events, keyword]);
  const clearFilters = (): void => {
    setKeyword("");
    setArea("");
    setPage(1);
  };

  const change = useMutation({
    mutationFn: ({
      typeName,
      channel,
      enabled,
    }: {
      typeName: string;
      channel: NotificationChannel;
      enabled: boolean;
    }) => setNotificationPolicyChannel(typeName, channel, enabled),
    onMutate: ({ typeName, channel }) => {
      setActionError(null);
      setPending(`${typeName}:${channel}`);
    },
    /* Refetched rather than patched from the response. A change can move the OTHER
       channel's derived state, and re-reading is one round trip against a list this size
       — cheaper than a merge that has to know which derivations exist. */
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: notificationPolicyQueryKey }),
    onError: (error) => setActionError(errorCopy(error)),
    onSettled: () => setPending(null),
  });

  const pageCount = Math.max(
    1,
    Math.ceil(filteredEvents.length / POLICY_PAGE_SIZE),
  );
  /* Clamped rather than reset, matching the delivery table: a refetch that shrinks the
     list must not strand the administrator on a page that no longer exists. */
  const currentPage = Math.min(page, pageCount);
  const visible = useMemo(
    () =>
      filteredEvents.slice(
        (currentPage - 1) * POLICY_PAGE_SIZE,
        currentPage * POLICY_PAGE_SIZE,
      ),
    [filteredEvents, currentPage],
  );

  const columns = useMemo(
    () => [
      {
        id: "event",
        width: "32%",
        /* Column floors (PB-5436): each is this column's width at a 1024px window
           with the rail closed, so the table's minimum is its 1024px width. A
           narrower box (the Player AI rail at 1024-1152px) scrolls inside the
           pinned region instead of clipping; wider boxes are unchanged. */
        minWidth: 202,
        header: "Event",
        cell: (row: NotificationPolicyEvent) => {
          const label = labelForNotificationType(row.typeName);
          return (
            /* The registry name is deliberately absent. It is a stable identifier for the
               API and the database, not English, and an administrator deciding what their
               people receive should never have to read one. */
            <div className="flex flex-col">
              <span className="text-dense-1 font-medium text-pb-text">
                {label.title}
              </span>
              {label.description ? (
                <span className="text-dense-2 text-pb-text-muted">
                  {label.description}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        /* Replaces the Area column. Area stays as a FILTER, which is where the
           ticket puts it — the module an event belongs to is how you narrow a
           long list, not a fact worth a column on a three-row table, whereas
           who receives it is the thing an administrator is deciding about. */
        id: "recipient",
        width: "14%",
        minWidth: 88,
        header: "Recipient",
        cell: (row: NotificationPolicyEvent) => (
          <span className="text-dense-1 text-pb-text">
            {EVENT_RECIPIENTS[row.typeName] ?? "—"}
          </span>
        ),
      },
      {
        id: "in_app",
        width: "20%",
        minWidth: 126,
        header: "In-app",
        cell: (row: NotificationPolicyEvent) => (
          <ChannelControl
            event={row}
            channel="in_app"
            pending={pending}
            onChange={change.mutate}
          />
        ),
      },
      {
        id: "email",
        width: "20%",
        minWidth: 126,
        header: "Email",
        cell: (row: NotificationPolicyEvent) => (
          <ChannelControl
            event={row}
            channel="email"
            pending={pending}
            onChange={change.mutate}
            globallyDisabled={!emailSendingEnabled}
          />
        ),
      },
      {
        /* This said a constant "Ready" in healthy green, on the argument that
           the three MVP rows are unconditionally present so no state could
           differ. The argument was wrong, and a browser capture is what showed
           it: on a workspace with no active mail server and sending paused, the
           table showed three green Ready rows — one of them on an event whose
           email channel is unavailable and says so two columns to the left.
           The flags that differ were in the row all along. */
        id: "state",
        width: "14%",
        minWidth: 88,
        header: "State",
        cell: (row: NotificationPolicyEvent) => {
          const state = eventNotificationState(row, emailReach);
          return <StatusPill tone={state.tone}>{state.label}</StatusPill>;
        },
      },
      {
        id: "actions",
        width: 64,
        minWidth: 64,
        header: <span className="sr-only">Actions</span>,
        align: "end" as const,
        cardRole: "actions" as const,
        cell: (row: NotificationPolicyEvent) => {
          const label = labelForNotificationType(row.typeName);
          const email = channelOf(row, "email");
          return (
            /* AC6 wants an ActionMenu on every Actions column with a
               row-specific triggerLabel and reason-carrying items. The ticket
               does not enumerate this tab's actions, so the menu offers the one
               genuinely useful navigation — this event's own delivery history —
               and keeps the wording edit visible-but-reasoned where the event
               has no editable template. Unavailable items stay in the menu
               rather than disappearing, so the absence is explained. */
            <ActionMenu
              triggerLabel={`Actions for ${label.title}`}
              triggerTestId={`event-actions-${row.typeName}`}
              items={[
                {
                  id: "history",
                  label: "View delivery history",
                  ...(email?.control === "unavailable"
                    ? {
                        disabledReason:
                          "This event has no email message, so it has no delivery history.",
                      }
                    : {}),
                  onSelect: () => onViewHistory(row),
                },
              ]}
            />
          );
        },
      },
    ],
    [pending, change.mutate, emailSendingEnabled, emailReach, onViewHistory],
  );

  return (
    <section className="flex flex-col gap-3" data-testid="notification-policy">
      {/* Promises what the screen actually offers, which changed with PB-2937.
       *
       * This said "Email settings arrive once PlaybookAI can send mail", correct while
       * `EMAIL_DELIVERY_AVAILABLE` was false and every email cell rendered fixed. That
       * flag is true now: email is switchable wherever the event has a catalogue
       * message. Leaving the header alone while correcting the Alert nineteen lines
       * below produced two sentences contradicting each other on one screen — the same
       * PB-47 dead-switch failure this comment was written to prevent, only inverted:
       * copy denying a control the administrator can plainly use. */}
      {/* h2 and hidden: this page already has its own title, and the tab
          immediately above says "Notifications". */}
      <SectionHeader
        headingLevel={2}
        titleHidden
        title="Notification settings"
        description="Choose what your organisation is notified about, in the app and by email."
      />

      {actionError ? (
        <Alert tone="critical" title="That change did not save">
          {actionError}
        </Alert>
      ) : null}

      {/* Stated once, here, rather than repeated per disabled row. An administrator who
          cannot see why an email cell is switched off and fixed would otherwise read it
          as a fault.
       *
       * The reason given has to be the one the ROWS give, and that changed with PB-2937.
       * This said "PlaybookAI has no way to send it", which was true while
       * `EMAIL_DELIVERY_AVAILABLE` was false and every cell was fixed for that one
       * reason. The send pipeline landed and flipped that flag, so email is now offered
       * wherever the event HAS a catalogue message, and the fixed cells give
       * `REASON_NO_EMAIL_MESSAGE` instead. Left alone, this Alert would have told an
       * administrator email was impossible on a screen that was letting them switch it
       * on — and on the same page where they edit the wording it sends. */}
      <Alert tone="info" title="Some settings are fixed">
        Email is offered for the events {PRODUCT_NAME} has a message for; the
        rest say so beneath them. A few in-app notices are always on because
        they carry something the recipient has to act on.
      </Alert>

      {policy.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : policy.isError ? (
        <Alert tone="critical" title="Could not load the notification policy">
          {errorCopy(policy.error)}
        </Alert>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Search notifications">
              <Input
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setPage(1);
                }}
                placeholder="Search by notification name"
                data-testid="notification-keyword-filter"
              />
            </FormField>
            <FormField label="Area" data-testid="notification-area-filter">
              <Select
                options={areaOptions}
                value={area}
                onChange={(value) => {
                  setArea(value);
                  setPage(1);
                }}
              />
            </FormField>
          </div>
          <FilterBar
            filters={[
              ...(keyword.trim()
                ? [{ id: "keyword", label: "Search", value: keyword.trim() }]
                : []),
              ...(area
                ? [
                    {
                      id: "area",
                      label: "Area",
                      value:
                        areaOptions.find((option) => option.value === area)
                          ?.label ?? area,
                    },
                  ]
                : []),
            ]}
            onRemove={(id) => {
              if (id === "keyword") setKeyword("");
              if (id === "area") setArea("");
              setPage(1);
            }}
            onClearAll={clearFilters}
          />
          <p className="text-dense-2 text-pb-text-muted" aria-live="polite">
            Showing {filteredEvents.length} of {events.length}{" "}
            {events.length === 1 ? "event" : "events"}
          </p>
          <DataTable
            layout="fixed"
            columns={columns}
            data={visible}
            getRowId={(row) => row.typeName}
            textSize="small"
            /* No `nowrap`: the channel cells carry a sentence explaining why a
               switch is off, so they have to wrap. With nowrap they either
               widened the table past its container or, once the columns were
               declared, clipped mid-sentence. */
            pinFirstColumn
            caption="Notification policy by event and channel"
            emptyState={
              keyword.trim() || area ? (
                <EmptyState
                  variant="no-results"
                  title="No notification settings match"
                  description="Try a different search or clear the filters."
                  action={
                    <Button variant="outline" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title="No notification types registered"
                  description="Nothing has declared a notification type yet, so there is nothing to configure."
                />
              )
            }
          />

          {filteredEvents.length > 0 ? (
            <div
              className="flex flex-wrap items-center justify-between gap-2"
              data-testid="notification-policy-pagination"
            >
              <span className="text-dense-2 text-pb-text-muted">
                {filteredEvents.length}{" "}
                {filteredEvents.length === 1 ? "event" : "events"}
              </span>
              {pageCount > 1 ? (
                <Pagination
                  mode="numbered"
                  page={currentPage}
                  pageCount={pageCount}
                  onChange={setPage}
                />
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * One channel cell.
 *
 * Three visually distinct outcomes, deliberately: a real control, an always-on statement,
 * and a not-possible statement. The reason text comes from the server so the explanation
 * an administrator reads is the same fact the server enforced.
 */
function ChannelControl({
  event,
  channel,
  pending,
  onChange,
  globallyDisabled = false,
}: {
  event: NotificationPolicyEvent;
  channel: NotificationChannel;
  pending: string | null;
  onChange: (input: {
    typeName: string;
    channel: NotificationChannel;
    enabled: boolean;
  }) => void;
  globallyDisabled?: boolean;
}): ReactElement {
  const decision = channelOf(event, channel);
  if (!decision) return <span className="text-pb-text-muted">—</span>;

  const busy = pending === `${event.typeName}:${channel}`;
  const channelName = channel === "in_app" ? "In-app" : "Email";
  /* The accessible name carries the EVENT, not only the channel. `DataTable` renders the
     event cell as a plain `<td>`, so nothing relates a switch back to its row: a screen
     reader listing the form controls on this page would otherwise read fifty identical
     "Email notifications", leaving no way to tell which one is about to be flipped. Taken
     from the same lookup the visible cell uses, so the spoken name and the printed one
     cannot drift apart. */
  const eventTitle = labelForNotificationType(event.typeName).title;

  /* A DISABLED switch, not prose. An administrator scanning the column is comparing
     switch positions, and a row that answers in words forces them to re-read it to learn
     something the other rows show at a glance.
   *
   * `locked` shows ON, `unavailable` shows OFF — distinct by POSITION as well as by
   * caption, so the two never read alike.
   *
   * The reason is rendered as TEXT and tied to the control with `aria-describedby`, not
   * carried in a `title`. A disabled switch is not focusable, so a tooltip on it cannot
   * be reached by keyboard at all, and assistive technology does not reliably announce
   * `title` on a wrapper. Hover was the only path to the explanation, which made the
   * "explains why" promise in the section's own Alert true only for mouse users. */
  if (decision.control !== "switchable") {
    const locked = decision.control === "locked";
    const reasonId = `policy-${channel}-${event.typeName}-reason`;
    return (
      <div className="flex flex-col gap-0.5">
        <Switch
          checked={locked}
          disabled
          /* The same two words the caption below shows. These read "always
             on" / "not available" while the caption said "Required" / "Not
             used", so a screen reader and the screen named one state
             differently — the split AC3 is about, across modalities. */
          aria-label={`${channelName} — ${
            locked ? "required" : "not available"
          } — ${eventTitle}`}
          aria-describedby={decision.reason ? reasonId : undefined}
          data-testid={`policy-${channel}-${event.typeName}`}
        />
        <span className="text-dense-2 text-pb-text-muted">
          {/* "Required" says WHY it cannot be changed, which "Always on" did
              not. "Not available" stays as it was: this channel cannot carry
              this event at all, and "Not used" would read as a choice somebody
              made — the distinction this control exists to keep. */}
          {locked ? "Required" : "Not available"}
        </span>
        {decision.reason ? (
          <span id={reasonId} className="text-dense-2 text-pb-text-muted">
            {decision.reason}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <Switch
        checked={decision.enabled}
        disabled={busy || globallyDisabled}
        onChange={(next) =>
          onChange({ typeName: event.typeName, channel, enabled: next })
        }
        aria-label={`${channelName} notifications — ${eventTitle}`}
        data-testid={`policy-${channel}-${event.typeName}`}
      />
      {globallyDisabled ? (
        <span className="text-dense-2 text-pb-text-muted">
          Workspace email is paused
        </span>
      ) : null}
    </div>
  );
}

/**
 * The messages PlaybookAI can send: what each one says, and the tenant's own
 * wording where they have replaced it.
 *
 * Every rendering shown here comes from the SERVER, through the same code a
 * real send uses — this component never lays a message out itself, because a
 * second rendering path would drift from the real one and the drift would only
 * surface when a recipient complained. That holds for a draft the
 * administrator has not saved yet, which is rendered by the same endpoint.
 */
function MessageCatalogue(): ReactElement {
  const tenantName = useAuthStore((s) => s.claims?.tenantName) ?? "";
  /* ONE piece of state, not two.
     
     These were `previewKey` and `editingKey`, and the mutual exclusion between
     them lived in the handlers: `onEdit` closed the preview, `onToggle` did not
     close the editor, so Edit-then-Preview rendered both — a live draft above a
     staler copy of the same message from the server. The same asymmetry has now
     been found three times in this component in different guises, always as a
     guard present in one direction only.
     
     Modelling it as "which panel, on which message" makes both-open
     unrepresentable rather than something two handlers have to keep agreeing
     about. */
  const [openPanel, setOpenPanel] = useState<{
    key: string;
    panel: "preview" | "editor";
  } | null>(null);
  const [keyword, setKeyword] = useState("");
  const [state, setState] = useState("");
  const [readiness, setReadiness] = useState("");
  const [page, setPage] = useState(1);

  const isOpen = (key: string, panel: "preview" | "editor"): boolean =>
    openPanel?.key === key && openPanel.panel === panel;

  /** Opens this panel, or closes it if it is already the open one. */
  const toggle = (key: string, panel: "preview" | "editor"): void =>
    setOpenPanel(isOpen(key, panel) ? null : { key, panel });

  const messages = useQuery({
    queryKey: mailMessagesQueryKey,
    queryFn: listMailMessages,
  });
  const allMessages = useMemo(() => messages.data ?? [], [messages.data]);
  /* The message the open panel is about, resolved once. The panel used to be
     derived by filtering the whole catalogue at the render site and mapping
     over a one-element array, which read as a list and was not one. */
  const openMessage = openPanel
    ? (allMessages.find((message) => message.key === openPanel.key) ?? null)
    : null;
  /* The modal is mounted for the life of this page and closed by its `open`
     prop (ADR-0091), so everything its body reads has to outlive the state that
     opened it: `openPanel` is already null on the frame the modal is animating
     out, and reading `openMessage.title` there would throw. Retained, so the
     closing frame is drawn from the panel that was open — including the `key`,
     which would otherwise change mid-exit and remount the body as it leaves. */
  const shownPanel = useRetainedValue(openPanel);
  const shownMessage = shownPanel
    ? (allMessages.find((message) => message.key === shownPanel.key) ?? null)
    : null;
  const filteredMessages = useMemo(() => {
    const term = keyword.trim().toLocaleLowerCase();
    return allMessages.filter((message) => {
      /* Searches the TRIGGER too, which the ticket asks for and the placeholder
         now promises. `message.key` stays in the haystack — it is a raw
         identifier nobody is shown, but somebody who knows one can still find
         its row with it. */
      const matchesKeyword =
        term === "" ||
        [
          message.title,
          message.description,
          message.key,
          MESSAGE_TRIGGERS[message.key] ?? "",
        ].some((value) => value.toLocaleLowerCase().includes(term));
      /* THREE wording states, not two. "Fixed" is a different axis from
         customised: set-password is security-controlled, so it is neither
         Default nor Customised and the old two-way split filed it under
         Default — offering "Default" and silently including a message whose
         wording can never be either. */
      const editable = message.editable ?? message.key !== "set_password";
      const matchesState =
        state === "" ||
        (state === "fixed"
          ? !editable
          : state === "customised"
            ? editable && message.customised
            : editable && !message.customised);
      const matchesReadiness =
        readiness === "" ||
        (readiness === "ready" ? message.sendable : !message.sendable);
      return matchesKeyword && matchesState && matchesReadiness;
    });
  }, [allMessages, keyword, readiness, state]);
  const resetView = (): void => {
    setOpenPanel(null);
  };
  const clearFilters = (): void => {
    setKeyword("");
    setState("");
    setReadiness("");
    setPage(1);
    resetView();
  };
  const pageCount = Math.max(
    1,
    Math.ceil(filteredMessages.length / MESSAGE_PAGE_SIZE),
  );
  const currentPage = Math.min(page, pageCount);
  const visibleMessages = filteredMessages.slice(
    (currentPage - 1) * MESSAGE_PAGE_SIZE,
    currentPage * MESSAGE_PAGE_SIZE,
  );
  const messageColumns = [
    {
      id: "message",
      width: "30%",
      /* Column floors (PB-5436): each is this column's width at a 1024px window
         with the rail closed, so the table's minimum is its 1024px width. A
         narrower box (the Player AI rail at 1024-1152px) scrolls inside the
         pinned region instead of clipping; wider boxes are unchanged. */
      minWidth: 208,
      header: "Template",
      cardRole: "title" as const,
      cell: (message: MailMessageSummary) => (
        <div
          className="flex min-w-0 flex-col"
          data-testid={`message-${message.key}`}
        >
          <span className="truncate text-dense-1 font-medium text-pb-text">
            {message.title}
          </span>
          <span className="line-clamp-2 text-dense-2 text-pb-text-muted">
            {message.description}
          </span>
        </div>
      ),
    },
    {
      /* What causes the message to be sent, as its own column.
       *
         Copy keyed on the message, not a field: `MailMessageSummary` carries
         title, description, variables and flags but nothing naming the trigger,
         and `notificationType` is a registry identifier rather than English. So
         this is the frontend vocabulary layer, the same choice as the Recipient
         column on the Notification events tab. Four entries because the tab
         shows exactly the four MVP messages; an unknown key renders nothing
         rather than guessing at another module’s trigger. */
      id: "trigger",
      width: "18%",
      minWidth: 125,
      header: "Trigger",
      cell: (message: MailMessageSummary) => (
        <span className="text-dense-1 text-pb-text">
          {MESSAGE_TRIGGERS[message.key] ?? "—"}
        </span>
      ),
    },
    {
      id: "content",
      width: "18%",
      minWidth: 125,
      header: "Wording",
      cardRole: "badge" as const,
      cell: (message: MailMessageSummary) =>
        (message.editable ?? message.key !== "set_password") ? (
          <StatusPill tone={message.customised ? "info" : "neutral"}>
            {message.customised ? "Customised" : "Default"}
          </StatusPill>
        ) : (
          <StatusPill tone="neutral">Fixed</StatusPill>
        ),
    },
    {
      id: "delivery",
      width: "18%",
      minWidth: 125,
      header: "Readiness",
      cell: (message: MailMessageSummary) => (
        <StatusPill tone={message.sendable ? "healthy" : "watch"}>
          {message.sendable ? "Ready to send" : "Not ready"}
        </StatusPill>
      ),
    },
    {
      id: "actions",
      width: 64,
      minWidth: 111,
      header: <span className="sr-only">Actions</span>,
      align: "end" as const,
      cardRole: "actions" as const,
      cell: (message: MailMessageSummary) => {
        const editable = message.editable ?? message.key !== "set_password";
        return (
          <ActionMenu
            triggerLabel={`Actions for ${message.title}`}
            triggerTestId={`message-actions-${message.key}`}
            items={[
              {
                id: "preview",
                label: isOpen(message.key, "preview")
                  ? "Close preview"
                  : "Preview",
                onSelect: () => toggle(message.key, "preview"),
              },
              ...(editable
                ? [
                    {
                      id: "edit",
                      label: isOpen(message.key, "editor")
                        ? "Close editor"
                        : "Edit wording",
                      onSelect: () => toggle(message.key, "editor"),
                    },
                  ]
                : [
                    {
                      id: "edit",
                      label: "Edit wording",
                      disabledReason:
                        "Set password content is fixed for security and can only be previewed.",
                    },
                  ]),
            ]}
          />
        );
      },
    },
  ];

  return (
    <section className="flex flex-col gap-3" data-testid="message-catalogue">
      <SectionHeader
        headingLevel={2}
        titleHidden
        title="Messages"
        description={`What ${PRODUCT_NAME} sends, how each one reads, and the wording you have made your own.`}
      />

      {messages.isError ? (
        <Alert tone="critical" title="Couldn't load the message list">
          {errorCopy(messages.error)}
          <Button
            variant="secondary"
            onClick={() => {
              void messages.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      ) : messages.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <FormField label="Search messages">
              <Input
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setPage(1);
                  resetView();
                }}
                placeholder="Search by title, description or trigger"
                data-testid="message-keyword-filter"
              />
            </FormField>
            <FormField label="Wording state" data-testid="message-state-filter">
              <Select
                value={state}
                options={[
                  { value: "", label: "All wording" },
                  { value: "customised", label: "Customised" },
                  { value: "default", label: "Default" },
                  { value: "fixed", label: "Fixed" },
                ]}
                onChange={(value) => {
                  setState(value);
                  setPage(1);
                  resetView();
                }}
              />
            </FormField>
            <FormField label="Readiness" data-testid="message-readiness-filter">
              <Select
                value={readiness}
                options={[
                  { value: "", label: "All readiness" },
                  { value: "ready", label: "Ready to send" },
                  { value: "not-ready", label: "Not ready" },
                ]}
                onChange={(value) => {
                  setReadiness(value);
                  setPage(1);
                  resetView();
                }}
              />
            </FormField>
          </div>
          <FilterBar
            filters={[
              ...(keyword.trim()
                ? [{ id: "keyword", label: "Search", value: keyword.trim() }]
                : []),
              ...(state
                ? [
                    {
                      id: "state",
                      label: "Wording",
                      /* The chip repeats the option that was chosen, so it
                         reads from the same three-word vocabulary as the
                         Select and the row pill. It said "Built-in wording"
                         while both of those said "Default", and it had no
                         spelling at all for the third option. */
                      value:
                        state === "customised"
                          ? "Customised"
                          : state === "fixed"
                            ? "Fixed"
                            : "Default",
                    },
                  ]
                : []),
              ...(readiness
                ? [
                    {
                      id: "readiness",
                      label: "Readiness",
                      value:
                        readiness === "ready" ? "Ready to send" : "Not ready",
                    },
                  ]
                : []),
            ]}
            onRemove={(id) => {
              if (id === "keyword") setKeyword("");
              if (id === "state") setState("");
              if (id === "readiness") setReadiness("");
              setPage(1);
              resetView();
            }}
            onClearAll={clearFilters}
          />
          <p className="text-dense-2 text-pb-text-muted" aria-live="polite">
            Showing {filteredMessages.length} of {allMessages.length}{" "}
            {allMessages.length === 1 ? "message" : "messages"}
          </p>
          <DataTable
            layout="fixed"
            columns={messageColumns}
            data={visibleMessages}
            getRowId={(message) => message.key}
            responsive
            pinFirstColumn
            textSize="small"
            caption="Email message templates"
            emptyState={
              keyword.trim() || state || readiness ? (
                <EmptyState
                  variant="no-results"
                  title="No messages match"
                  description="Try a different search or clear the filters."
                  action={
                    <Button variant="outline" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title="No email messages registered"
                  description="There is no email wording to configure yet."
                />
              )
            }
          />
          {pageCount > 1 ? (
            <div className="flex justify-center">
              <Pagination
                mode="numbered"
                page={currentPage}
                pageCount={pageCount}
                onChange={(next) => {
                  setPage(next);
                  resetView();
                }}
              />
            </div>
          ) : null}
          {/* A MODAL, not a panel appended under the table.
            *
              Both of these used to render BELOW the table and its pagination,
              so choosing Preview or Edit wording on row two scrolled an
              administrator past everything to a section that had grown the
              page — and on a phone the thing they asked for was off-screen
              entirely. Editing and details belong in a Modal or Drawer; a
              section that permanently grows the page is the shape this design
              system avoids, and the delivery-details view on the next tab
              already answers the same need this way.
            *
              Modal over Drawer because the editor is a FORM with its own Save
              and Restore: it wants the reader's whole attention and a clear
              cancel, which is what a modal's focus trap and dismiss give.
              Keyed on the panel so switching preview -> editor remounts rather
              than reusing a form's state across two different intents. */}
          <Modal
            open={openMessage !== null}
            onClose={() => setOpenPanel(null)}
            title={shownMessage?.title ?? ""}
            description={
              shownPanel?.panel === "editor"
                ? `Your wording replaces ${PRODUCT_NAME}'s for everyone in this workspace.`
                : "Exactly as a recipient would receive it, with sample values."
            }
            size="lg"
          >
            {shownMessage ? (
              <MessageRow
                key={`${shownMessage.key}-${shownPanel?.panel}`}
                message={shownMessage}
                workspace={tenantName}
                open={shownPanel?.panel === "preview"}
                editing={shownPanel?.panel === "editor"}
                onEdit={() => toggle(shownMessage.key, "editor")}
                onCloseEditor={() => setOpenPanel(null)}
              />
            ) : null}
          </Modal>
        </>
      )}
    </section>
  );
}

/**
 * The body of the preview/editor modal for one message.
 *
 * It no longer renders the message's own title, description and state pills:
 * those repeated the table row the reader just acted on, and inside a modal
 * that already carries the title as its heading they were said three times.
 * The modal owns the heading, the description and the dismiss; this owns the
 * content and nothing else.
 */
function MessageRow({
  message,
  workspace,
  open,
  editing,
  onEdit,
  onCloseEditor,
}: {
  message: MailMessageSummary;
  workspace: string;
  open: boolean;
  editing: boolean;
  onEdit: () => void;
  onCloseEditor: () => void;
}): ReactElement {
  const editable = message.editable ?? message.key !== "set_password";
  const preview = useQuery({
    queryKey: mailPreviewQueryKey(message.key, workspace),
    queryFn: () => previewMailMessage(message.key, workspace),
    // Only fetched once the administrator asks to see it.
    enabled: open,
  });

  return (
    /* No Card wrapper: a card inside a modal is a second border around content
       that is already framed, and the modal supplies the padding. The testid
       stays so the specs keep addressing this by the message it is about. */
    <div className="flex flex-col gap-3" data-testid={`message-${message.key}`}>
      {/* Preview -> editor without closing and re-opening, which is the one
          thing the removed header carried that the modal does not. Offered only
          while previewing, and only where the wording can actually change. */}
      {open && editable ? (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={onEdit}
            data-testid={`edit-${message.key}`}
          >
            Edit wording
          </Button>
        </div>
      ) : null}

      {editing ? (
        <MessageEditor
          message={message}
          workspace={workspace}
          onDone={onCloseEditor}
        />
      ) : null}

      {open ? (
        preview.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : preview.isError ? (
          <Alert tone="critical" title="Could not render the preview">
            {errorCopy(preview.error)}
          </Alert>
        ) : (
          <div
            // Same as the editor: the Preview button's `aria-controls` needs a
            // real id to point at.
            id={`preview-body-${message.key}`}
            className="flex flex-col gap-2 rounded-md border border-pb-border bg-pb-card-elevated p-3"
            data-testid={`preview-body-${message.key}`}
          >
            <div className="text-dense-2 text-pb-text-muted">
              Subject:{" "}
              <span className="text-pb-text">{preview.data?.subject}</span>
            </div>
            {/* Pre-wrapped: the body IS plain text, and reflowing it would
                  stop being "exactly as a recipient would receive it". */}
            <pre className="whitespace-pre-wrap font-sans text-dense-2 text-pb-text">
              {preview.data?.body}
            </pre>
            <p className="text-dense-2 text-pb-text-muted">
              Sample values shown. Real sends use the recipient's own details.
            </p>
          </div>
        )
      ) : null}
    </div>
  );
}

/**
 * Editing one message.
 *
 * The draft lives in local state and is sent to the server to be rendered; the
 * server is also the authority on whether it can be saved. Refusals are shown
 * verbatim because they NAME the variable that is missing, which is the one
 * thing an administrator needs in order to fix their own text.
 */
/**
 * "{{a}}", "{{a}} and {{b}}", "{{a}}, {{b}} and {{c}}".
 *
 * Joining with " and " throughout reads as a chant once a message uses four
 * values, which the invitation does.
 */
function listOfVariables(variables: string[]): string {
  const named = variables.map((v) => `{{${v}}}`);
  if (named.length <= 1) return named.join("");
  return `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
}

/**
 * Names the variables one field may not lose, or says nothing if it may lose any.
 *
 * Carries an `id` so the input it describes can point at it with
 * `aria-describedby`. Sitting next to a box is a sighted reader's cue only —
 * without the association, someone who focuses the Subject field hears the
 * label and nothing about what has to stay in it, which is the one thing that
 * will get their save refused.
 */
function RequiredHere({
  variables,
  testId,
  id,
}: {
  variables: string[];
  testId: string;
  id: string;
}): ReactElement | null {
  if (variables.length === 0) return null;
  return (
    <p
      id={id}
      className="-mt-2 text-dense-2 text-pb-text-muted"
      data-testid={testId}
    >
      <span className="text-pb-text">{listOfVariables(variables)}</span>{" "}
      {variables.length === 1 ? "has" : "have"} to stay in this field — the
      message cannot be sent without {variables.length === 1 ? "it" : "them"}.
    </p>
  );
}

function MessageEditor({
  message,
  workspace,
  onDone,
}: {
  message: MailMessageSummary;
  workspace: string;
  onDone: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<{ subject: string; body: string } | null>(
    null,
  );
  const [rendered, setRendered] = useState<MailMessagePreview | null>(null);
  /* The refusal to SHOW, held here rather than read off the mutation.

     Clearing it used to call `save.reset()`, which detaches the mutation
     observer: `isPending` went false while the request was still in flight, so
     typing after clicking Save re-enabled Save AND Restore and a second write
     could be issued. Keeping the message in local state means the pending flags
     stay true for as long as the request actually runs. */
  /* The ACTION as well as the error. One shared slot is deliberate — `update()`
     clears it when the text changes, which is why a failed restore no longer
     lingers over an edited draft — but a single hard-coded title announced a
     failed preview and a failed restore as "That change cannot be saved",
     naming an action the administrator had not asked for (PB-4987). */
  const [failure, setFailure] = useState<{
    action: "save" | "preview" | "restore";
    error: unknown;
  } | null>(null);

  const detail = useQuery({
    queryKey: mailMessageQueryKey(message.key),
    queryFn: () => getMailMessage(message.key),
  });

  /* Seeded from the server's EFFECTIVE template once, not held in sync with it.
     Re-seeding on every render of fresh data would discard whatever the
     administrator had typed the moment any refetch landed. */
  const current = draft ?? {
    subject: detail.data?.subject ?? "",
    body: detail.data?.body ?? "",
  };

  function update(patch: Partial<{ subject: string; body: string }>): void {
    setDraft({ ...current, ...patch });
    // The rendering belongs to the text that produced it. Keeping it on screen
    // after an edit would show a preview of something no longer being edited.
    setRendered(null);
    /* And so does the refusal. A rejection names a variable that is missing
       from text the administrator has since changed, so leaving it up says
       their current draft is invalid when it may not be — worse, it sits
       directly above a successful preview of that same draft.

       Clearing the STATE, never resetting the mutation: a reset would untrack
       an in-flight save and let a second one be issued on top of it. */
    setFailure(null);
  }

  /**
   * Only the fields that actually differ from the shipped default are sent.
   *
   * This is the whole sparse-storage decision surfacing in the UI: a subject
   * left exactly as PlaybookAI wrote it is sent as null, so it keeps tracking
   * that wording as it improves rather than freezing a copy of today's.
   */
  /* Compared TRIMMED. An exact comparison made a stray trailing space a real
     override: the row stored the shipped wording verbatim, which FROZE it — a
     later change to the shipped default would never reach this tenant — and the
     message earned a "Customised" badge the administrator could not account
     for, from an edit they could not see. Only the decision about whether an
     override EXISTS ignores surrounding whitespace; a genuine edit is still
     stored exactly as typed (PB-4987 M8). */
  function overrideRequest(): MailMessageOverrideRequest {
    const d = detail.data;
    const matchesShipped = (value: string, shipped: string): boolean =>
      value.trim() === shipped.trim();
    return {
      subject:
        d && matchesShipped(current.subject, d.defaultSubject)
          ? null
          : current.subject,
      body:
        d && matchesShipped(current.body, d.defaultBody) ? null : current.body,
    };
  }

  const previewDraft = useMutation({
    mutationFn: () =>
      /* The text ON SCREEN, not `overrideRequest()`.

         Sparseness is a STORAGE decision: that helper nulls a field whose text
         equals the shipped default, and the preview endpoint reads a null field
         as "use what is stored for this tenant". So for a message that already
         has a saved override, editing a field back to the shipped wording would
         have previewed the old stored text rather than what the administrator
         was looking at. Save is unaffected — a null there is what clears the
         override — but a preview has to show the draft verbatim. */
      previewMailMessageDraft(message.key, {
        subject: current.subject,
        body: current.body,
        workspace,
      }),
    onSuccess: (result) => {
      setRendered(result);
      setFailure(null);
    },
    onError: (error) => setFailure({ action: "preview", error }),
  });

  const save = useMutation({
    mutationFn: () => saveMailMessageOverride(message.key, overrideRequest()),
    onError: (error) => setFailure({ action: "save", error }),
    onSuccess: async () => {
      /* One call. `mailMessagesQueryKey` IS `["mail", "messages"]`, and
         TanStack matches keys by PREFIX, so this already invalidates the
         per-message `mailMessageQueryKey(key)` entries too. The second call was
         the same key written out by hand. */
      await queryClient.invalidateQueries({ queryKey: mailMessagesQueryKey });
      onDone();
    },
  });

  const revert = useMutation({
    mutationFn: () => revertMailMessageOverride(message.key),
    /* Same state as the save failure, so `update()` clears it when the text
       changes. Read straight off `revert.error` it had nothing to clear it: a
       failed restore stayed on screen while the administrator edited and
       previewed, reporting a refusal for text no longer in the box — and could
       render beside the save Alert with a different title for one action. */
    onError: (error) => setFailure({ action: "restore", error }),
    onSuccess: async () => {
      /* One call. `mailMessagesQueryKey` IS `["mail", "messages"]`, and
         TanStack matches keys by PREFIX, so this already invalidates the
         per-message `mailMessageQueryKey(key)` entries too. The second call was
         the same key written out by hand. */
      await queryClient.invalidateQueries({ queryKey: mailMessagesQueryKey });
      onDone();
    },
  });

  /**
   * Which values this FIELD may not lose: the ones its shipped copy used.
   *
   * The server refuses an edit that drops any of them, so saying "required"
   * without saying WHERE would send an administrator hunting through the wrong
   * box. Read off the defaults the server already returns rather than asking
   * for a second list that could disagree with them.
   */
  function requiredIn(field: "subject" | "body"): string[] {
    const shipped =
      field === "subject"
        ? detail.data?.defaultSubject
        : detail.data?.defaultBody;
    if (!shipped) return [];
    const found: string[] = [];
    for (const match of shipped.matchAll(/\{\{([A-Za-z0-9_]+)\}\}/g)) {
      const name = match[1] as string;
      if (!found.includes(name)) found.push(name);
    }
    return found;
  }

  if (detail.isLoading) return <Skeleton className="h-64 w-full" />;
  if (detail.isError) {
    return (
      <Alert tone="critical" title="Could not open this message">
        {errorCopy(detail.error)}
      </Alert>
    );
  }

  /* Both fields back at the shipped wording, so there is no override to store.
     The server refuses this with a named 400 and the database forbids the row
     outright, so offering Save here can only produce a refusal for something
     the administrator plainly meant. Where they already have an override, what
     they have actually asked for is a revert, and the button for that is right
     there. */
  const request = overrideRequest();
  const nothingToSave = request.subject === null && request.body === null;

  /* Save and Restore both write, and they write the OPPOSITE thing. Leaving
     each enabled while the other is in flight lets both requests go before
     either answers, and which override survives then depends on the order the
     server happens to finish them in. */
  const changing = save.isPending || revert.isPending;

  return (
    <div
      /* `id`, not only `data-testid`. The Edit button points here with
         `aria-controls`, and a reference to a testid resolves to nothing. */
      id={`editor-${message.key}`}
      className="flex flex-col gap-3 rounded-md border border-pb-border bg-pb-card-elevated p-3"
      data-testid={`editor-${message.key}`}
    >
      {failure ? (
        <Alert tone="critical" title={FAILURE_TITLES[failure.action]}>
          {errorCopy(failure.error)}
        </Alert>
      ) : null}

      <FormField label="Subject" htmlFor={`subject-${message.key}`}>
        <Input
          id={`subject-${message.key}`}
          data-testid={`subject-${message.key}`}
          value={current.subject}
          maxLength={200}
          aria-describedby={
            requiredIn("subject").length > 0
              ? `required-subject-hint-${message.key}`
              : undefined
          }
          onChange={(e) => update({ subject: e.target.value })}
        />
      </FormField>
      <RequiredHere
        variables={requiredIn("subject")}
        testId={`required-subject-${message.key}`}
        id={`required-subject-hint-${message.key}`}
      />

      <FormField label="Body" htmlFor={`body-${message.key}`}>
        <Textarea
          id={`body-${message.key}`}
          data-testid={`body-${message.key}`}
          rows={10}
          value={current.body}
          maxLength={5000}
          aria-describedby={
            requiredIn("body").length > 0
              ? `required-body-hint-${message.key}`
              : undefined
          }
          onChange={(e) => update({ body: e.target.value })}
        />
      </FormField>
      <RequiredHere
        variables={requiredIn("body")}
        testId={`required-body-${message.key}`}
        id={`required-body-hint-${message.key}`}
      />

      {/* No meta-placeholder here, deliberately. This used to read "Write
          {{name}} where a value should go", where `name` stood for "whichever
          value you want" — but it renders in exactly the same braces as the
          real values listed in the very next clause, so it reads as a fifth
          available one. An administrator followed it literally, typed
          {{name}}, and got "\"name\" is not a value this message has" from
          `mail-message.service.ts`. The validator was right; the instruction
          was wrong. Describing the SYNTAX instead of demonstrating it with a
          fake value removes the ambiguity rather than trying to typographically
          signal it. */}
      <p
        className="text-dense-2 text-pb-text-muted"
        data-testid={`variables-${message.key}`}
      >
        Wrap a value in double braces to insert it. This message can use{" "}
        {message.variables.map((v) => `{{${v}}}`).join(", ")}.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          onClick={() => previewDraft.mutate()}
          disabled={previewDraft.isPending}
          data-testid={`preview-draft-${message.key}`}
        >
          {previewDraft.isPending ? "Rendering…" : "Preview draft"}
        </Button>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={changing || nothingToSave}
          data-testid={`save-${message.key}`}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        {/* `detail.data`, not the list row. Every other value in this editor
            comes from the per-message fetch; the catalogue list can be stale —
            after another session reverts the same message it would offer
            Restore for a message holding no override, and the request 404s. */}
        {detail.data?.customised ? (
          <Button
            variant="outline"
            onClick={() => revert.mutate()}
            disabled={changing}
            data-testid={`revert-${message.key}`}
          >
            {revert.isPending ? "Restoring…" : "Restore built-in wording"}
          </Button>
        ) : null}
      </div>

      {nothingToSave ? (
        <p
          className="text-dense-2 text-pb-text-muted"
          data-testid={`nothing-to-save-${message.key}`}
        >
          {detail.data?.customised
            ? "This is the built-in wording. Restore it to stop keeping your own copy."
            : "This is the built-in wording — nothing to save yet."}
        </p>
      ) : null}

      {rendered ? (
        <div
          className="flex flex-col gap-2 rounded-md border border-pb-border bg-pb-card-elevated p-3"
          data-testid={`draft-preview-${message.key}`}
        >
          <div className="text-dense-2 text-pb-text-muted">
            Subject: <span className="text-pb-text">{rendered.subject}</span>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-dense-2 text-pb-text">
            {rendered.body}
          </pre>
          <p className="text-dense-2 text-pb-text-muted">
            Your unsaved draft, rendered with sample values by the same code a
            real send uses.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A retired server’s last real send, through the shared formatter.
 *
 * Kept when the collapsed history sections were folded into the main table:
 * the value is the one an administrator uses to attribute old mail, so losing
 * it with the cards would have removed a fact rather than a layout.
 */
function formatLastProductionUse(
  sender: MailSender,
  dateTimeOr: (value: string | null, absent: string) => string,
): string {
  return dateTimeOr(sender.lastProductionUseAt, "No production sends recorded");
}

/**
 * The same fields are used for adding and editing inside one shared modal.
 *
 * One form rather than two, because the fields, their rules and their hints are
 * identical — and the edit path is rendered in place of the draft card it
 * belongs to, with its OWN state, so opening an edit cannot discard a half-typed
 * new configuration.
 */
function AddSenderForm({
  form,
  setForm,
  fieldErrors,
  onFieldEdited,
  error,
  submitting,
  onSubmit,
  submitLabel,
  notice,
  onCancel,
  formTestId,
  secretExamples,
}: {
  form: typeof EMPTY_FORM;
  setForm: (next: typeof EMPTY_FORM) => void;
  fieldErrors: SenderFieldErrors;
  onFieldEdited: (field: keyof typeof EMPTY_FORM) => void;
  error: string | null;
  /** Distinct per form: the page can hold an add and an edit at once. */
  formTestId?: string;
  submitting: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  submitLabel?: string;
  notice?: string;
  onCancel?: () => void;
  secretExamples?:
    | Partial<Record<"azure_key_vault" | "aws_secrets_manager", string>>
    | undefined;
}): ReactElement {
  const set = <K extends keyof typeof EMPTY_FORM>(
    key: K,
    value: (typeof EMPTY_FORM)[K],
  ): void => {
    onFieldEdited(key);
    setForm({ ...form, [key]: value });
  };

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="flex flex-col gap-4"
      data-testid={formTestId ?? "add-sender-form"}
    >
      {/* Said BEFORE saving, not discovered afterwards: an edit clears the
              stored test result, so a draft that was ready to activate will not
              be until it passes another test. */}
      {notice ? <Alert tone="info">{notice}</Alert> : null}

      {error ? (
        <Alert tone="critical" title="Check the configuration">
          {error}
        </Alert>
      ) : null}

      <div className="grid min-w-0 gap-4 sm:grid-cols-2">
        <FormField
          label="Server display name"
          error={fieldErrors.displayName}
          hint={`Optional. If blank, ${PRODUCT_NAME} uses the From name, address, or host.`}
          data-field="displayName"
        >
          <Input
            name="displayName"
            value={form.displayName}
            onChange={(e) => set("displayName", e.target.value)}
            placeholder="Production SMTP"
            data-testid="field-display-name"
          />
        </FormField>
        <FormField
          label="Mail server address"
          required
          error={fieldErrors.host}
          data-field="host"
        >
          <Input
            name="host"
            value={form.host}
            onChange={(e) => set("host", e.target.value)}
            placeholder="smtp.example.com"
            data-testid="field-host"
          />
        </FormField>
        <FormField
          label="Port"
          required
          error={fieldErrors.port}
          data-field="port"
        >
          <Input
            name="port"
            value={form.port}
            onChange={(e) => set("port", e.target.value)}
            inputMode="numeric"
            data-testid="field-port"
          />
        </FormField>
        <FormField
          label="Security"
          error={fieldErrors.securityMode}
          data-field="securityMode"
        >
          <Select
            name="securityMode"
            options={SECURITY_OPTIONS}
            value={form.securityMode}
            onChange={(value) => set("securityMode", value as MailSecurityMode)}
          />
        </FormField>
        <FormField
          label="Username"
          error={fieldErrors.username}
          hint="Leave blank if the server needs no sign-in."
          data-field="username"
        >
          <Input
            name="username"
            value={form.username}
            onChange={(e) => set("username", e.target.value)}
            data-testid="field-username"
          />
        </FormField>
        <FormField
          label="Secret reference"
          error={fieldErrors.secretReference}
          required
          hint={`Paste a versionless Azure Key Vault secret URL or AWS Secrets Manager ARN supported by this deployment. The provider is detected automatically; ${PRODUCT_NAME} never receives the password.`}
          data-field="secretReference"
        >
          <Input
            name="secretReference"
            value={form.secretReference}
            onChange={(e) => set("secretReference", e.target.value)}
            placeholder={
              secretExamples?.azure_key_vault ??
              secretExamples?.aws_secrets_manager ??
              "https://your-vault.vault.azure.net/secrets/smtp-password"
            }
            data-testid="field-secret-reference"
          />
        </FormField>
        <FormField
          label="Send from address"
          error={fieldErrors.fromAddress}
          required
          data-field="fromAddress"
        >
          <Input
            name="fromAddress"
            value={form.fromAddress}
            onChange={(e) => set("fromAddress", e.target.value)}
            placeholder="no-reply@example.com"
            data-testid="field-from-address"
          />
        </FormField>
        <FormField
          label="Sender display name"
          error={fieldErrors.fromDisplayName}
          data-field="fromDisplayName"
        >
          <Input
            name="fromDisplayName"
            value={form.fromDisplayName}
            onChange={(e) => set("fromDisplayName", e.target.value)}
            placeholder="Example Corp"
            data-testid="field-from-display-name"
          />
        </FormField>
        {/* PB-2938. Optional, and the hint says why it is asked for: the
                CUSTOMER's server signs the mail, so only they know which
                selector it publishes — PlaybookAI cannot derive it. Without a
                value the DKIM check has nowhere to look and reports
                "not configured" rather than a failure. */}
        <FormField
          label="DKIM selector (optional)"
          error={fieldErrors.dkimSelector}
          hint="The selector your mail server signs with, so DKIM can be checked. Your server publishes it at <selector>._domainkey.<your domain>."
          data-field="dkimSelector"
        >
          <Input
            name="dkimSelector"
            value={form.dkimSelector}
            onChange={(e) => set("dkimSelector", e.target.value)}
            placeholder="selector1"
            data-testid="field-dkim-selector"
          />
        </FormField>
      </div>
      <div className="flex flex-wrap justify-end gap-2 border-t border-pb-border pt-4">
        {/* No explicit `type` needed, and that is worth knowing rather than
              guessing: `packages/ui`'s Button applies `type ?? "button"`, so a
              control inside this real <form> does not default to submit the way
              a bare <button> would. Only the submit button below states its
              type. A spec asserts the resulting attribute, so a change to that
              shared default is caught here rather than by Cancel quietly saving
              the edit it exists to abandon.

              Cancel leads and the primary trails, matching the two
              confirmations on this page. */}
        {onCancel ? (
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="cancel-sender-editor"
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          data-testid="save-sender"
        >
          {submitLabel ?? "Save configuration"}
        </Button>
      </div>
    </form>
  );
}

/**
 * The four admin-facing filters. The search API keeps sender/event fields for
 * other clients, but this page deliberately omits those low-value controls.
 */
interface DeliveryFilters {
  recipient: string;
  status: "" | MailDelivery["outcome"];
  /* "" is every event. Each non-empty value is mapped below to the identifier
     its delivery row actually stores: registry eventType or addressed
     messageKey. */
  event: string;
  submittedFrom: string;
  submittedTo: string;
}

const EMPTY_DELIVERY_FILTERS: DeliveryFilters = {
  recipient: "",
  status: "",
  event: "",
  submittedFrom: "",
  submittedTo: "",
};

/**
 * The Event filter's options — the MVP messages and nothing else.
 *
 * Each option names the storage identifier its delivery rows actually carry.
 * Invitations use the catalogue message key because addressed sends have no
 * registry event type; the other MVP rows use their registry type.
 */
/** Every event the page lists, named exactly as the Event column names it.
 *
 *  Derived rather than written out: the two hand-written entries read "Access
 *  granted" and "Export ready" where the column rendered "Someone is given
 *  access" and "An export is ready to download", so filtering to an event and
 *  reading the result said two different things about one event (AC3). */
const DELIVERY_EVENT_OPTIONS = [
  { value: "", label: "All events" },
  ...MVP_EVENT_TYPES.map((typeName) => ({
    value: typeName,
    label: labelForNotificationType(typeName).title,
    ...(typeName === "invitation"
      ? { messageKey: "invitation" }
      : { eventType: typeName }),
  })),
];

/** One vocabulary for a delivery outcome, shared by the row pill, the status
    filter, the summary tile and the detail view — so the same state never
    reads two different ways on one page (AC3). */
const DELIVERY_LABEL: Readonly<Record<MailDelivery["outcome"], string>> = {
  accepted: "Accepted",
  rejected: "Rejected",
  pending: "Processing",
  skipped: "Skipped",
};

const DELIVERY_TONE: Readonly<
  Record<MailDelivery["outcome"], "healthy" | "critical" | "watch" | "neutral">
> = {
  accepted: "healthy",
  rejected: "critical",
  pending: "watch",
  skipped: "neutral",
};

/**
 * The actionable error and its next step, in full.
 *
 * Lifted out of the table cell it used to be inlined in, wording unchanged —
 * the text was right, its container was not.
 */
function deliveryReason(row: MailDelivery): string {
  if (row.outcome === "pending") {
    return "The result was not confirmed. Check the server before retrying.";
  }
  if (row.outcome === "skipped") {
    return "Workspace email sending was paused. No SMTP connection was attempted.";
  }
  if (!row.failureReason) return "No failure was reported.";
  return `${row.failureReason} ${retryGuidance(row.failureRetryable)}`;
}

/**
 * The next-step half of a failure, on its own.
 *
 * Lifted out of `deliveryReason` (PB-5169) because a server's test detail needs
 * the guidance WITHOUT the reason — it already shows the reason the sender row
 * recorded, and repeating it would print the same sentence twice. One source,
 * so the two surfaces cannot drift into saying different things about the same
 * stored verdict.
 */
function retryGuidance(retryable: boolean | null): string {
  return retryable === true
    ? "You can try again."
    : retryable === false
      ? "Fix the recipient or server setup before retrying."
      : "Retry guidance is not available.";
}

/** The tone and word for a recorded test outcome, shared by the Last tested
 *  cell and the detail it opens so the two always agree. */
function testTone(
  outcome: MailSender["lastTestOutcome"],
): "healthy" | "critical" | "neutral" {
  return outcome === "passed"
    ? "healthy"
    : outcome === "failed"
      ? "critical"
      : "neutral";
}

function testLabel(outcome: MailSender["lastTestOutcome"]): string {
  return outcome === "passed"
    ? "Passed"
    : outcome === "failed"
      ? "Failed"
      : "Not tested";
}

const DELIVERY_PAGE_SIZE = 20;

/** Widen one selected calendar day to the reader's full configured day. */
function toDeliveryInstant(
  day: string,
  edge: "start" | "end",
  timeZone: string,
): string | null {
  if (day === "") return null;
  const start = startOfDayInZone(day, timeZone);
  if (start === null) return null;
  if (edge === "start") return new Date(start).toISOString();

  /* The next local midnight minus 1 ms also works across 23/25-hour DST days. */
  const nextDay = isoDateInZone(start + 36 * 60 * 60 * 1000, timeZone);
  const nextStart = startOfDayInZone(nextDay, timeZone);
  return nextStart === null ? null : new Date(nextStart - 1).toISOString();
}

function deliveryRequest(
  filters: DeliveryFilters,
  cursor: string | null,
  timeZone: string,
): MailDeliverySearchRequest {
  const selectedEvent = DELIVERY_EVENT_OPTIONS.find(
    (option) => option.value === filters.event,
  );
  return {
    senderId: null,
    outcome: filters.status || null,
    ...(selectedEvent && "eventType" in selectedEvent
      ? { eventType: selectedEvent.eventType }
      : {}),
    ...(selectedEvent && "messageKey" in selectedEvent
      ? { messageKey: selectedEvent.messageKey }
      : {}),
    submittedFrom: toDeliveryInstant(filters.submittedFrom, "start", timeZone),
    submittedTo: toDeliveryInstant(filters.submittedTo, "end", timeZone),
    recipient: filters.recipient.trim() || null,
    limit: DELIVERY_PAGE_SIZE,
    cursor,
  };
}

function DeliveryResults({
  event,
}: {
  /* Set when an administrator arrived from a Notification events row action, so
     the list opens already answering the question they asked. */
  event: string | null;
}): ReactElement {
  const [draft, setDraft] = useState<DeliveryFilters>(EMPTY_DELIVERY_FILTERS);
  const [applied, setApplied] = useState<DeliveryFilters>(
    EMPTY_DELIVERY_FILTERS,
  );
  const [dateError, setDateError] = useState<string | null>(null);
  const [detail, setDetail] = useState<MailDelivery | null>(null);
  /* Stable, so the columns memo is not rebuilt on every keystroke in a filter. */
  const onOpenDetail = useCallback((row: MailDelivery) => setDetail(row), []);
  const formatter = useDateTimeFormatter();

  /* Seed BOTH the draft and the applied filter when an administrator arrives
     from a Notification events row action, so the list they land on is already
     the answer AND the control shows why it is narrowed. Keyed on the incoming
     value, so clearing the filter afterwards is theirs to do and this does not
     undo it on the next render. */
  useEffect(() => {
    if (!event) return;
    setDraft({ ...EMPTY_DELIVERY_FILTERS, event });
    setApplied({ ...EMPTY_DELIVERY_FILTERS, event });
  }, [event]);

  /* The `previous` and `archived` inventory reads that used to live here are
     gone with the name map they fed (PB-5368). They could never answer for a
     DISCARDED server — it is in no group this console lists — so the row it
     sent showed "Unknown server". The delivery response carries the resolved
     name now, which also drops two paged fetches from every visit here. */
  /* TRUE cursor pagination, one page at a time, replacing an infinite query
     that accumulated every fetched page into one growing list behind a bespoke
     "Load more" button.
   *
     AC5 requires cursor mode for the keyset endpoints, and the approved mockup
     shows a numbered 1 2 3 … 22 control. Those cannot both be built: a keyset
     cursor has no way to jump to page 17 — it only knows the row after the one
     it was given — so numbered paging would need offset paging and a different
     server contract. Cursor wins on feasibility, not preference, and the shared
     Pagination renders Previous/Next for it.
   *
     Keyed on the applied filters and the time zone, so changing a filter resets
     paging rather than stranding the reader on a page that no longer exists. */
  const paging = useCursorPagination(
    `${JSON.stringify(applied)}|${formatter.timeZone}`,
    { pageSize: DELIVERY_PAGE_SIZE },
  );
  const history = useQuery({
    queryKey: [
      ...mailDeliveriesQueryKey,
      "search",
      applied,
      formatter.timeZone,
      paging.cursor,
    ],
    queryFn: () =>
      searchMailDeliveries(
        deliveryRequest(applied, paging.cursor ?? null, formatter.timeZone),
      ),
  });

  /* ONE page now, not an accumulation of every page fetched so far. */
  const deliveries = history.data?.deliveries ?? [];
  const totals = history.data?.totals;
  const columns = useMemo(
    () => [
      {
        /* Column widths, because a fixed-layout table below its design width
           clips rather than scrolls, and without hints these six divided
           evenly — which spent the same room on a mail-server name as on a
           timestamp and clipped "Accepted by server" and "Sep 18, 2026, 6:13
           PM" by ~30px each at 1024.
         *
           Recipient absorbs the loss deliberately: it is the column an
           administrator FILTERS by rather than reads, and a clipped address is
           recoverable in the details view every row opens — the same
           reachability test that moved the reason column there. Status and When
           are what a log is scanned by, so they keep their full text. */
        id: "recipient",
        width: "21%",
        /* Column floors (PB-5436): each is this column's width at a 1024px window
           with the rail closed, so the table's minimum is its 1024px width. A
           narrower box (the Player AI rail at 1024-1152px) scrolls inside the
           pinned region instead of clipping; wider boxes are unchanged. */
        minWidth: 141,
        header: "Recipient",
        cell: (row: MailDelivery) => row.recipientAddress,
      },
      {
        /* "Event", matching the filter beside it and the tab that links here.
           This column said "Message" while the filter said "Event" and the tab
           said "Notification events" — three names for one concept. */
        id: "message",
        width: "19%",
        minWidth: 127,
        header: "Event",
        cell: (row: MailDelivery) => deliveryEventLabel(row),
      },
      {
        id: "sender",
        width: "11%",
        minWidth: 74,
        header: "Mail server",
        /* The fallback is for the rolling-deployment window alone: a pod that
           predates the field. Every current response carries a name. */
        cell: (row: MailDelivery) =>
          row.senderResolvedDisplayName ?? "Unknown server",
      },
      {
        id: "outcome",
        width: "21%",
        minWidth: 141,
        header: "Status",
        cell: (row: MailDelivery) => {
          const presentation =
            row.outcome === "accepted"
              ? { label: "Accepted by server", tone: "healthy" as const }
              : row.outcome === "rejected"
                ? { label: "Rejected", tone: "critical" as const }
                : row.outcome === "skipped"
                  ? { label: "Skipped", tone: "neutral" as const }
                  : { label: "Unconfirmed", tone: "watch" as const };
          return (
            <StatusPill tone={presentation.tone}>
              {presentation.label}
            </StatusPill>
          );
        },
      },
      {
        id: "submitted",
        width: "22%",
        minWidth: 147,
        header: <AdminTimeZoneLabel label="When" formatter={formatter} />,
        cell: (row: MailDelivery) => formatter.dateTime(row.submittedAt),
      },
      {
        /* The "Reason and next step" column that used to sit between Status and
           When is gone, and its content is not: it moved into the details view
           this column opens. It was a 28rem truncating cell whose full text was
           only reachable as a title attribute — unreadable to a screen reader,
           unreachable by keyboard, and the widest thing on the table. The
           ticket puts the actionable error and next step in the details for
           exactly that reason. */
        id: "actions",
        width: 64,
        minWidth: 64,
        header: <span className="sr-only">Actions</span>,
        align: "end" as const,
        cardRole: "actions" as const,
        cell: (row: MailDelivery) => (
          /* Shared Button rather than a bespoke icon control: @playbookai/ui
             exports no general IconButton, and inventing one here is precisely
             what AC2 forbids. The visible glyph carries an accessible name that
             names the row, because every row offers the same icon. */
          <Button
            variant="ghost"
            onClick={() => onOpenDetail(row)}
            aria-label={`Delivery details for ${row.recipientAddress}`}
            data-testid={`delivery-detail-${row.id}`}
          >
            <Eye className="size-4" aria-hidden="true" />
          </Button>
        ),
      },
    ],
    [formatter, onOpenDetail],
  );

  const setDraftField = <Key extends keyof DeliveryFilters>(
    key: Key,
    value: DeliveryFilters[Key],
  ): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setDateError(null);
  };
  const applyFilters = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (
      draft.submittedFrom &&
      draft.submittedTo &&
      draft.submittedFrom > draft.submittedTo
    ) {
      setDateError("From date must be on or before To date.");
      return;
    }
    setDateError(null);
    setApplied({ ...draft, recipient: draft.recipient.trim() });
  };
  const clearFilters = (): void => {
    setDraft(EMPTY_DELIVERY_FILTERS);
    setApplied(EMPTY_DELIVERY_FILTERS);
    setDateError(null);
  };
  const removeFilter = (id: string): void => {
    if (!(id in EMPTY_DELIVERY_FILTERS)) return;
    const key = id as keyof DeliveryFilters;
    const next = { ...applied, [key]: EMPTY_DELIVERY_FILTERS[key] };
    setApplied(next);
    setDraft(next);
    setDateError(null);
  };
  const activeFilters = [
    ...(applied.recipient
      ? [{ id: "recipient", label: "Recipient", value: applied.recipient }]
      : []),
    ...(applied.status
      ? [
          {
            id: "status",
            label: "Status",
            value:
              applied.status === "accepted"
                ? "Accepted by server"
                : applied.status === "rejected"
                  ? "Rejected"
                  : applied.status === "skipped"
                    ? "Skipped"
                    : "Unconfirmed",
          },
        ]
      : []),
    ...(applied.submittedFrom
      ? [{ id: "submittedFrom", label: "From", value: applied.submittedFrom }]
      : []),
    ...(applied.submittedTo
      ? [{ id: "submittedTo", label: "To", value: applied.submittedTo }]
      : []),
  ];

  return (
    <section className="flex flex-col gap-3" data-testid="delivery-results">
      {/* This panel owns a filter form and result status, so its heading stays
          explicit even though the containing tab carries the same label. */}
      <SectionHeader
        headingLevel={2}
        title="Delivery history"
        description={`Search the messages ${PRODUCT_NAME} submitted to your mail server.`}
      />

      {/* Three columns from `lg` up, not two. With two, the fifth control wrapped
          alone onto a third row and the two date fields landed on DIFFERENT
          rows — From beside Event, To beneath it — so the pair an administrator
          fills together read as unrelated. Three columns keep Recipient, Status
          and Event on one row and the date pair together on the next, and spend
          one row less of the vertical space this tab was short of. */}
      <form
        noValidate
        className="grid gap-3 sm:grid-cols-2 @min-[42.5rem]/settings-page:grid-cols-3"
        onSubmit={applyFilters}
        data-testid="delivery-filters"
      >
        <FormField label="Recipient">
          <Input
            value={draft.recipient}
            onChange={(event) => setDraftField("recipient", event.target.value)}
            placeholder="name@example.com"
            data-testid="delivery-recipient-filter"
          />
        </FormField>
        <FormField label="Status" data-testid="delivery-status-filter">
          <Select
            value={draft.status}
            /* "Processing", matching the summary tile above and the row pill.
               This said "Unconfirmed" while the tile said something else again,
               which is the inconsistency AC3 is about. */
            options={[
              { value: "", label: "All statuses" },
              { value: "accepted", label: "Accepted by server" },
              { value: "rejected", label: "Rejected" },
              { value: "pending", label: "Processing" },
              { value: "skipped", label: "Skipped" },
            ]}
            onChange={(value) =>
              setDraftField("status", value as DeliveryFilters["status"])
            }
          />
        </FormField>
        {/* The Event/message-type filter the ticket asks for. The wire contract
            maps each visible event to the eventType or messageKey its delivery
            rows actually store. */}
        <FormField label="Event" data-testid="delivery-event-filter">
          <Select
            value={draft.event}
            options={[...DELIVERY_EVENT_OPTIONS]}
            onChange={(value) => setDraftField("event", value)}
          />
        </FormField>
        <FormField
          label="From date"
          error={dateError ?? undefined}
          data-testid="delivery-from-filter"
        >
          <DatePicker
            value={draft.submittedFrom}
            onChange={(day) => setDraftField("submittedFrom", day)}
            {...(draft.submittedTo ? { max: draft.submittedTo } : {})}
            placeholder="Any earlier date"
          />
        </FormField>
        <FormField label="To date" data-testid="delivery-to-filter">
          <DatePicker
            value={draft.submittedTo}
            onChange={(day) => setDraftField("submittedTo", day)}
            {...(draft.submittedFrom ? { min: draft.submittedFrom } : {})}
            max={isoDateInZone(Date.now(), formatter.timeZone)}
            placeholder="Up to today"
          />
        </FormField>
        <div className="flex items-end gap-2 sm:col-span-2">
          <Button type="submit" variant="primary">
            Apply filters
          </Button>
          {activeFilters.length > 0 ? (
            <Button variant="outline" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : null}
        </div>
      </form>

      <FilterBar
        filters={activeFilters}
        onRemove={removeFilter}
        onClearAll={clearFilters}
      />

      {history.isError ? (
        <Alert tone="critical" title="Couldn't load the delivery results">
          {errorCopy(history.error)}
          <Button
            variant="secondary"
            onClick={() => {
              void history.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      ) : (
        <>
          {/* The ticket's four compact counts, as tiles rather than a run of
              inline text — the same summary treatment the Mail server tab uses,
              so the two tabs read as one page.
            *
              ALWAYS four, never conditional. Processing and Skipped used to
              render only when non-zero, which is the wrong default for a count:
              a missing tile is indistinguishable from a tile reading 0, and "no
              messages are stuck" is precisely the reassurance an administrator
              came for. Submitted is the matching total, so the four are a
              partition of it rather than four unrelated numbers.
            *
              Skipped is a fifth fact and it stays: it is this branch's own
              terminal state for a paused workspace, and the ticket's list
              predates it. Dropping it to match a four-item spec would hide the
              outcome most likely to puzzle somebody who just paused sending. */}
          <dl
            className="grid grid-cols-2 gap-3 @min-[58.5rem]/settings-page:grid-cols-5"
            aria-live="polite"
            data-testid="delivery-summary"
          >
            {[
              { label: "Submitted", value: totals?.matching.total, tone: null },
              {
                label: "Accepted by server",
                value: totals?.matching.accepted,
                tone: "healthy" as const,
              },
              {
                label: "Rejected",
                value: totals?.matching.rejected,
                tone: "critical" as const,
              },
              {
                label: "Processing",
                value: totals?.matching.pending,
                tone: "watch" as const,
              },
              {
                label: "Skipped",
                value: totals?.matching.skipped,
                tone: "neutral" as const,
              },
            ].map((tile) => (
              <div
                key={tile.label}
                className="flex flex-col gap-1 rounded-xl border border-pb-border bg-pb-card-elevated p-3"
              >
                <dt className="flex items-center gap-2 text-dense-2 text-pb-text-muted">
                  {tile.tone ? (
                    <span
                      className={`size-2 rounded-full ${
                        /* The real theme tokens — `--pb-success`/`--pb-danger`/`--pb-warning`
                           are not defined, so those class names would have
                           rendered an invisible dot. These are the same four
                           StatusPill itself uses for the same four tones. */
                        tile.tone === "healthy"
                          ? "bg-pb-green"
                          : tile.tone === "critical"
                            ? "bg-pb-red"
                            : tile.tone === "watch"
                              ? "bg-pb-amber"
                              : "bg-pb-border"
                      }`}
                      aria-hidden="true"
                    />
                  ) : null}
                  {tile.label}
                </dt>
                <dd className="text-2xl font-semibold text-pb-text pb-tabular">
                  {history.isLoading ? "—" : (tile.value ?? 0)}
                </dd>
              </div>
            ))}
          </dl>

          <Alert tone="info" title="Accepted does not mean delivered">
            This records the mail server&apos;s immediate answer. It does not
            prove the message reached the inbox, or that the recipient opened or
            acted on it.
          </Alert>

          <DataTable
            layout="fixed"
            columns={columns}
            data={deliveries}
            getRowId={(row) => row.id}
            loading={history.isLoading}
            textSize="small"
            nowrap
            pinFirstColumn
            caption="Messages submitted to the mail server"
            emptyState={
              activeFilters.length > 0 ? (
                <EmptyState
                  variant="no-results"
                  title="No deliveries match"
                  description="Try different filters or clear them."
                  action={
                    <Button variant="outline" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title="Nothing sent yet"
                  description="Send a test message, or wait for the first email notification."
                />
              )
            }
          />

          {!history.isLoading && totals ? (
            <div
              className="flex flex-wrap items-center justify-between gap-2"
              data-testid="delivery-pagination"
            >
              <span className="text-dense-2 text-pb-text-muted">
                Showing {deliveries.length} of {totals.matching.total} matching
                · {totals.overall.total} total
              </span>
              <Pagination
                mode="cursor"
                page={paging.page}
                pageItemCount={deliveries.length}
                canGoBack={paging.canGoBack}
                hasNext={Boolean(history.data?.pagination.nextCursor)}
                busy={history.isFetching}
                onPrev={paging.goBack}
                onNext={() => {
                  const cursor = history.data?.pagination.nextCursor;
                  if (cursor) paging.goNext(cursor);
                }}
              />
            </div>
          ) : null}
        </>
      )}

      {/* Where the removed "Reason and next step" column went, plus the rest of
          what the ticket asks a delivery detail to show. Full text, not a 28rem
          truncation whose remainder lived only in a title attribute — that was
          unreadable to a screen reader and unreachable by keyboard.
        *
          No secrets and no raw identifiers, and deliberately no retry control:
          the ticket forbids offering one for invitation, password or export
          mail until idempotency and token safety are proven. */}
      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title="Delivery details"
        {...(detail ? { description: detail.recipientAddress } : {})}
      >
        {detail ? (
          /* Look row OK-25: the label/value pairs are the DescriptionList. */
          <DescriptionList data-testid="delivery-detail">
            <DescriptionItem label="Outcome">
              <StatusPill tone={DELIVERY_TONE[detail.outcome]}>
                {DELIVERY_LABEL[detail.outcome]}
              </StatusPill>
            </DescriptionItem>
            <DescriptionItem label="Mail server">
              {detail.senderResolvedDisplayName ?? "A removed mail server"}
            </DescriptionItem>
            <DescriptionItem
              label={
                <AdminTimeZoneLabel
                  label="Submitted"
                  formatter={formatter}
                  variant="inline"
                />
              }
            >
              {formatter.dateTime(detail.submittedAt)}
            </DescriptionItem>
            <DescriptionItem label="Reason and next step">
              {deliveryReason(detail)}
            </DescriptionItem>
          </DescriptionList>
        ) : null}
      </Modal>
    </section>
  );
}
